// Arenas.js

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useAppConfig } from "../ConfigContext";
import { motion, AnimatePresence } from "framer-motion";
import ArenasHub from "./ArenasHub";
import CardPicker from "./CardPicker";
import LoadingSpinner from "./LoadingSpinner";
import GamesTopBar from "./GamesTopBar";
import { getSkinSrc } from "./SeasonalSkins";
import MyTapisPage from "./MyTapisPage";

const API_BASE = process.env.REACT_APP_API_URL || "";
const BATTLE_CINEMATIC_MS = 900;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function formatCurrencyShort(value) {
  const num = Number(value || 0);

  if (!Number.isFinite(num)) return "0";

  if (num < 1000) {
    return String(Math.floor(num));
  }

  const units = [
    { value: 1_000_000_000, suffix: "B" },
    { value: 1_000_000, suffix: "M" },
    { value: 1_000, suffix: "K" },
  ];

  const unit = units.find((u) => num >= u.value);
  const short = num / unit.value;

  const formatted =
    short >= 100
      ? short.toFixed(0)
      : short.toFixed(1);

  return `${formatted.replace(/\.0$/, "")}${unit.suffix}`;
}

const stageVariants = {
  idle: { scale: 1, rotate: 0 },
  go: {
    scale: [1, 1.02, 1],
    rotate: [0, -0.15, 0.15, 0],
    transition: { duration: 0.38, ease: "easeOut" },
  },
};

const arenaPanelVariants = {
  idle: {
    scale: 1,
    filter: "brightness(1) contrast(1)",
  },
  go: {
    scale: 1.08,
    filter: "brightness(1.15) contrast(1.15) saturate(1.08)",
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

function toMsTimestamp(v, fallback = 0) {
  // already a finite number
  if (typeof v === "number" && Number.isFinite(v)) return v;

  // numeric string
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return fallback;

    const asNum = Number(trimmed);
    if (Number.isFinite(asNum)) return asNum;

    // ISO date string etc.
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  // Date instance
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : fallback;
  }

  return fallback;
}

function isChestUnlockNotStartedClient(chest) {
  const earnedAtMs = toMsTimestamp(
    chest?.earnedAt ??
    chest?.earned_at ??
    chest?.createdAt ??
    chest?.created_at,
    0
  );

  const unlockAtMs = toMsTimestamp(
    chest?.unlockAt ??
    chest?.unlock_at ??
    chest?.unlockAtMs ??
    chest?.unlock_at_ms,
    0
  );

  if (!earnedAtMs || !unlockAtMs) return true;

  return unlockAtMs <= earnedAtMs + 5000;
}

const flashVariants = {
  idle: { opacity: 0 },
  go: {
    opacity: [0, 0.9, 0],
    transition: { duration: 0.45, times: [0, 0.25, 1], ease: "easeOut" },
  },
};

/* =========================
   Styles
   ========================= */

const bgStyle = {
 
};

const pageTitleStyle = { fontSize: 42, margin: 0 };

const chestWrapStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "center",
  marginTop: 10,
  flexWrap: "nowrap",
  marginBottom: 0,
};

const chestCardStyle = {
  width: "calc(25% - 3px)",
  aspectRatio: "1 / 1",
  padding: 0,
  boxSizing: "border-box",
  borderRadius: 12,
  background: "transparent",
  border: "1px solid #8a623e",
  borderBottom: "4px solid #8a623e",
  fontSize: 12,
  overflow: "visible",
};

const chestLabelStyle = {
  textShadow: "-1px -1px 0 #5b2a00,1px -1px 0 #5b2a00,-1px  1px 0 #5b2a00,1px  1px 0 #5b2a00",
  color: "#fff",
  fontWeight: 900,
  fontSize: 11,
  marginTop: 10,
};

const rowWrapStyle = {
  width: "100%",
  padding: 5,
  boxSizing: "border-box",
  float: "left",
  marginBottom: 5,
};

const chestSlotFixedStyle = {
  flex: "0 0 calc((100% - 24px) / 4)",
  width: "calc((100% - 24px) / 4)",
  maxWidth: "calc((100% - 24px) / 4)",
  minWidth: 0,
};

const leftBoxBaseStyle = {
  float: "left",
  padding: 10,
  boxSizing: "border-box",
  position: "relative",
  borderRadius: 8,
  width: 60,
  height: 60,
  backgroundColor: "#1A0101",
};

const leftBoxCountStyle = {
  position: "absolute",
  bottom: 3,
  left: 0,
  width: "100%",
  textAlign: "center",
  color: "#fff",
  textShadow: "1px 1px #000",
  fontSize: 21,
};

const rightPanelBaseStyle = {
  float: "left",
  marginLeft: 4,
  padding: 10,
  boxSizing: "border-box",
  position: "relative",
  borderBottom: "2px solid #444",
  borderRadius: 8,
  width: "calc(100% - 64px)",
  height: 60,
};

const rightTitleBaseStyle = {
  position: "absolute",
  top: 7,
  left: 10,
  width: "calc(100% - 20px)",
  height: 18,
  lineHeight: "18px",
  color: "#fff",
  fontSize: 19,
  textAlign: "left",
};

const rightIconBaseStyle = {
  height: 18,
  position: "absolute",
  top: 7,
  right: 10,
};

const progressOuterStyle = {
  backgroundColor: "#000",
  overflow: "hidden",
  position: "absolute",
  bottom: 7,
  left: 10,
  width: "calc(100% - 20px)",
  height: 18,
  borderRadius: 9,
  color: "#fff",
  lineHeight: "19px",
  textShadow: "1px 1px #000",
  fontSize: 14,
};

const rewardsWithBarWrapStyle = {
  width: "100%",
  display: "flex",
  alignItems: "stretch",
  gap: 8,
};

const verticalBarColStyle = {
  width: 26,
  position: "relative",
  marginLeft: 2,
};

const verticalTrackStyle = {
  position: "absolute",
  top: 0,
  bottom: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: 8,
  borderRadius: 999,
  background: "rgba(0,0,0,0.6)",
  border: "1px solid rgba(255,255,255,0.14)",
  overflow: "hidden",
};

const verticalFillStyle = (pct) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: `${Math.max(0, Math.min(100, pct))}%`,
  background: "linear-gradient(180deg, #FD8700 0%, #BF1515 100%)",
});


const verticalMarkerDotStyle = {
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  width: 14,
  height: 14,
  borderRadius: 999,
  background: "#1A0101",
  border: "2px solid rgba(255,223,95,0.95)",
  boxShadow: "0 2px 6px rgba(0,0,0,0.6)",
};

const verticalMarkerLabelStyle = {
  position: "absolute",
  left: 22,
  transform: "translateY(-50%)",
  color: "rgba(255,255,255,0.85)",
  fontSize: 12,
  textShadow: "1px 1px #000",
  whiteSpace: "nowrap",
};


const progressTextStyle = { zIndex: 1, fontSize: 12, position: "relative",fontWeight: 600 };

const progressInnerBaseStyle = {
  borderRadius: 9,
  position: "absolute",
  top: 0,
  left: 0,
  background: "linear-gradient(90.59deg, #BF1515 1.87%, #FD8700 96.96%)",
  height: 18,
};

/* =========================
   Data + Helpers
   ========================= */

const CHESTS_META = {
  wood: {
    key: "wood",
    label: "WOOD",
    image: "chest1.png",
  },
  silver: {
    key: "silver",
    label: "SILVER",
    image: "chest2.png",
  },
  golden: {
    key: "golden",
    label: "GOLDEN",
    image: "chest3.png",
  },
  diamond: {
    key: "diamond",
    label: "DIAMOND",
    image: "chest4.png",
  },
};

const REWARD_UI = {
  sparks: { label: "Sparks", icon: "/sparks.png", quality: "common" },
  runes: { label: "Runes", icon: "/runes.png", quality: "rare" },
  cardPieces: { label: "Card Pieces", icon: "/pieces.png", quality: "common" },

  common: { label: "Common", icon: "/common.png", quality: "common" },
  rare: { label: "Rare", icon: "/rare.png", quality: "rare" },
  epic: { label: "Epic", icon: "/epic.png", quality: "epic" },
  legendary: { label: "Legendary", icon: "/legendary.png", quality: "legendary" },

  boosters: { label: "Booster", icon: "/energy.png", quality: "rare" },
  abilities: { label: "Ability", icon: "/fightlog.png", quality: "epic" },
  skins: { label: "Skin", icon: "/fragment.png", quality: "legendary" },
};

function normalizeChestKey(k) {
  const s = String(k ?? "").trim().toLowerCase();
  return CHESTS_META[s] ? s : "wood";
}

function getChestMetaFromChest(chest) {
  const key = normalizeChestKey(chest?.chestType);
  return CHESTS_META[key];
}

function mapBackendRewardsToPrizes(rewardsObj, openResult) {
  return Object.entries(rewardsObj ?? {})
    .map(([type, amount]) => {
      const meta = REWARD_UI[type] ?? { label: type, icon: "/pack.png", quality: "common" };

      const base = {
        id: `${type}_${Math.random().toString(16).slice(2)}`,
        type,
        label: meta.label,
        icon: meta.icon,
        amount: Number(amount) || 0,
        quality: meta.quality,
      };

      // ✅ Add card info ONLY for cardPieces
      if (type === "cardPieces") {
        return {
          ...base,
          cardId: openResult?.cardPiecesCardId ?? null,
          cardName: openResult?.cardPiecesCardName ?? null,
        };
      }

      return base;
    })
    .filter((p) => p.amount > 0);
}

function formatMsNoSeconds(ms) {
  const safeMs = Number.isFinite(ms) ? ms : 0;

  const totalSeconds = Math.max(0, Math.floor(safeMs / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, "0");
  if (h > 0) return `${pad(h)}:${pad(m)}`;
  return `${pad(m)}:${pad(s)}`;
}

/* =========================
   Components
   ========================= */

function FighterThumb({ card }) {
  const character = (() => {
    const raw = card?.character;
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return null;
  })();

  const layers = Object.entries(character || {}).sort(([a], [b]) => {
    const order = ["background", "body"];
    const ia = order.includes(a) ? order.indexOf(a) : order.length;
    const ib = order.includes(b) ? order.indexOf(b) : order.length;
    return ia - ib;
  });

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 4,
        background: "rgba(0,0,0,0.25)",
        position: "relative",
        boxSizing: "border-box",
        flex: "0 0 auto",
        overflow: "hidden",
      }}
    >
      {layers.length === 0 ? (
        <div
          style={{
            color: "#fff",
            opacity: 0.7,
            fontSize: 14,
            height: "100%",
            display: "grid",
            placeItems: "center",
          }}
        >
          No Fighter
        </div>
      ) : (
        layers.map(([part, data]) => {
          const d = data && typeof data === "object" ? data : {};
          const defaultSrc = `/assets/${part}/${d.side}/${d.rarity}/${d.variant}_thumb.png`;

const src = getSkinSrc(
  card?.seasonal_accessory?.season_name || card?.season_name || null,
  part,
  defaultSrc
);
          return (
            <img
              key={part}
              src={src}
              alt={part}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              loading="lazy"
              decoding="async"
            />
          );
        })
      )}
    </div>
  );
}

function ChestCountdown({ unlockAtMs }) {
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(id);
  }, []);

  const remainingMs = unlockAtMs - Date.now();
  return <>{formatMsNoSeconds(Math.max(0, remainingMs))}</>;
}

function getSpeedupPayment(cfg) {
  const runes = Number(cfg?.runes ?? 0) || 0;
  const sparks = Number(cfg?.sparks ?? 0) || 0;

  if (runes > 0) {
    return { currency: "runes", amount: runes, enabled: true };
  }

  if (sparks > 0) {
    return { currency: "sparks", amount: sparks, enabled: true };
  }

  return { currency: null, amount: 0, enabled: false };
}

function ChestCard({
  label,
  image,
  badgeText,
  showClock,
  speedupIcon = null,
  isClickable,
  onClick,
  isReady,
  isStart = false,
  disableGlow = false,
  cardStyle,
  appearDelay = 0,
  isProcessing = false,
}) {
  const isOpenBadge = typeof badgeText === "string" && badgeText === "OPEN";
const isUnlockBadge = typeof badgeText === "string" && badgeText === "UNLOCK";
const isClaimedBadge = typeof badgeText === "string" && badgeText === "CLAIMED";

const isCountdownBadge =
  !isOpenBadge &&
  !isUnlockBadge &&
  !isClaimedBadge &&
  (showClock || typeof badgeText !== "string");


  const badgeStyle = {
    textShadow: isOpenBadge
  ? "1px 2px 0 #145c2f"
  : isCountdownBadge
  ? "none"
  : "2px 2px #000",
    color: isCountdownBadge ? "#632712" : "#fff",
    fontSize: showClock ? 14 : 18,
    lineHeight: "24px",
    borderRadius: 8,
    margin: isUnlockBadge || isOpenBadge || isClaimedBadge || isCountdownBadge
  ? "-5px auto 0"
  : "3px auto 0",

backgroundColor: isCountdownBadge
  ? "#fff"
  : isClaimedBadge
  ? "#BF1515"
  : isReady || isStart
  ? "#2BB673"
  : "#DF5600",

borderBottom: isOpenBadge || isUnlockBadge
  ? "3px solid #145c2f"
  : isClaimedBadge
  ? "3px solid #5C0101"
  : isCountdownBadge
  ? "3px solid #632712"
  : "none",
    width: "100%",
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    flexShrink: 0,
  };

  return (
<motion.button
  type="button"
  initial={{ opacity: 0, y: 10, scale: 0.92 }}
  animate={{ opacity: 1, y: 0, scale: 1 }}
  transition={{
    duration: 0.25,
    ease: "easeOut",
    delay: appearDelay,
  }}
  onClick={onClick}
  disabled={!isClickable}
  style={{
    ...chestCardStyle,
    cursor: isClickable ? "pointer" : "default",
    opacity: isClickable ? 1 : 0.92,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    ...cardStyle,
    background: isOpenBadge ? "#fbb051" : "#f7ce9c",
    backgroundColor: isOpenBadge ? "#fbb051" : "#f7ce9c",
  }}
  className={isReady && !disableGlow && !isOpenBadge ? "open-chest" : ""}
>
      {/* TOP STATUS */}
{isOpenBadge ? (
  <img
    src="/v2/openchest.png"
    alt="Open"
    style={{
      width: "100%",
      height: "auto",
      margin: "3px auto 0",
      marginTop: -5,
      objectFit: "fill",
      display: "block",
      flexShrink: 0,
      pointerEvents: "none",
    }}
  />
) : (
  <div style={badgeStyle}>
    {showClock && (
      <img
        src="./clock.png"
        alt=""
        style={{
          verticalAlign: "middle",
          marginTop: 0,
          marginRight: -3,
          marginLeft: -3,
          height: 18,
        }}
      />
    )}

    <span
      style={{
        fontFamily: '"Fredoka", sans-serif',
        letterSpacing: 1,
        fontSize:
          typeof badgeText === "string" && badgeText === "CLAIMED"
            ? 11
            : 12,
        fontWeight: isOpenBadge || isCountdownBadge ? 900 : "bold",
      }}
    >
      {badgeText}
    </span>
  </div>
)}

      {/* CHEST IMAGE */}
<div
  style={{
    flex: 1,
    width: "100%",
    minHeight: 0,
    overflow: "hidden",
    borderRadius: "0 0 9px 9px",
  }}
>
<div
  className={isReady ? "chest-bounce" : ""}
  style={{
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  <img
    src={`/${image}`}
    alt=""
    style={{
      height: 48,
      width: "auto",
      maxWidth: "90%",
      objectFit: "contain",
      display: "block",
    }}
  />
</div>
</div>

      {/* BOTTOM LABEL */}
      <div
  style={{
    ...chestLabelStyle,
    marginTop: 0,
    marginBottom: 2,
    fontSize: 12,
    fontFamily: '"Fredoka", sans-serif',
    lineHeight: "12px",
    flexShrink: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  }}
>
  {isProcessing && (
    <motion.span
      animate={{ rotate: 360 }}
      transition={{
        duration: 0.7,
        repeat: Infinity,
        ease: "linear",
      }}
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        border: "2px solid rgba(255,255,255,0.35)",
        borderTopColor: "#fff",
        boxSizing: "border-box",
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  )}

  {label}
</div>
    </motion.button>
  );
}

function EmptyChestSlot({ style, loading = false }) {
  return (
    <div
      style={{
        ...chestCardStyle,
        ...chestSlotFixedStyle,
        ...style,
        background: "rgba(0,0,0,0.1)",
        border: "2px dashed #913D18",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
  style={{
    color: "#fff",
    textShadow: "1px 1px #000",
    fontSize: 14,
    fontFamily: '"Fredoka", sans-serif',
    fontWeight: 600,
  }}
>
  {loading ? "LOADING" : "EMPTY"}
</div>
    </div>
  );
}

function RewardRow({ leftIcon, count, borderColor, panelColor, title, rightIcon, showArrow, progressLabel, progressPct }) {
  return (
    <div style={rowWrapStyle}>
      <div style={{ ...leftBoxBaseStyle, border: `4px solid ${borderColor}` }}>
        <img src={leftIcon} alt="" style={{ width: "100%" }} />
        <div style={leftBoxCountStyle}>{count}</div>
      </div>

      <div style={{ ...rightPanelBaseStyle, backgroundColor: panelColor }}>
        <div style={rightTitleBaseStyle}>
          {showArrow && (
            <img
              src="./right.png"
              alt=""
              style={{
                marginRight: 5,
                verticalAlign: "middle",
                height: 18,
              }}
            />
          )}
          {title}
        </div>

        <img src={rightIcon} alt="" style={rightIconBaseStyle} />

        <div style={progressOuterStyle}>
          <span style={progressTextStyle}>{progressLabel}</span>
          <div style={{ ...progressInnerBaseStyle, width: `${Math.max(0, Math.min(100, progressPct))}%` }} />
        </div>
      </div>
    </div>
  );
}

/* =========================
   Modal
   ========================= */

function ChestOpenedModal({ open, chest, prizes, onClose, onNavigate }) {
  const chestLabel = chest?.label ?? "Chest";
  const chestImage = chest?.image ? `/${chest.image}` : null;
  const safePrizes = Array.isArray(prizes) ? prizes : [];

  return (
    <AnimatePresence>
      {open && (
        <div
          className="modal_box"
        >
          
            <div className="modal_title">New Chest Open
             <button
                  onClick={onClose}
                  className="modal_close"
                >
                </button></div>

            {/* Content */}
            <motion.div
              className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            >

              <div className="modal_inside">
              <img src="/v2/chest_open.png" style={{margin: 0,marginBottom: 10,marginTop: -10, height: 120}}/>

              <div
                style={{
                  textTransform: "uppercase",
                  marginBottom: 10,
                  fontSize: 28,
                  fontWeight: 600,
                  color: "#fff",
                   textShadow: `
              -1px -1px 0 #000,
               1px -1px 0 #000,
              -1px  1px 0 #000,
               1px  1px 0 #000,
               0px  3px 0 #000,
               0px  4px 2px rgba(0,0,0,0.75)
            `,
                  marginTop: -4,
                }}
              >
                {chestLabel?.toLowerCase().replace(/^\w/, c => c.toUpperCase())} CHEST
              </div>

              {/* Prize list */}
              <div
                className="info" style={{paddingTop: 1}}
              >
                {safePrizes.length > 0 ? (
                  safePrizes.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "rgba(0,0,0,0.25)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        marginTop: 10,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <img
                          src={p.icon}
                          alt=""
                          style={{ width: 34, height: 34, objectFit: "contain" }}
                        />
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontSize: 18, color: "#fff", lineHeight: "18px" }}>
                            {p.label}
                          </div>
                          {p.type === "cardPieces" && (p.cardName || p.cardId) && (
                            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.82)", marginTop: 2 }}>
                              {p.cardName ? p.cardName : `Card ID: ${p.cardId}`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          color: "#ffdf5f",
                          textShadow: "1px 1px #000",
                          whiteSpace: "nowrap",
                        }}
                      >
                        +{Number(p.amount ?? 0)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: "rgba(255,255,255,0.85)", marginTop: 10 }}>
                    No prizes rolled.
                  </div>
                )}
              </div>

              </div>

              {/* Collect */}
              <div
                className="modal_footer"
              >
              <button
                  onClick={() => {
                    onClose?.();
                    onNavigate?.("inventoryHub", { tab: "items" });
                  }}
                  className="game-button green-button" style={{marginRight: 15}}
                >
                  Inventory
                </button>
                <button
                  onClick={onClose}
                  className="game-button red-button"
                >
                  CLOSE
                </button>
              </div>
            </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function SimpleModal({ open, title, children, onClose, footer }) {
  return (
    <AnimatePresence>
      {open && (
        <div
          className="modal_box"
        >

            <div className="modal_title">
              {title}
              <button className="modal_close" onClick={onClose}></button>
            </div>


            <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

              <div className="modal_inside">{children}</div>

            <div className="modal_footer">
              {footer ?? <button className="game-button red-button" onClick={onClose}>CLOSE</button>}
            </div>
                              </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function FightPickerModal({
  open,
  user,
  onClose,
  onSelectDone,
  onNavigate,
}) {
  return (
    <AnimatePresence>
      {open && (
        <div
          className="modal_box"
          style={{
            zIndex: 9999,
          }}
        >
          <div className="modal_title">
            Choose Your Fighter
            <button
              onClick={onClose}
              aria-label="Close"
              className="modal_close"
            />
          </div>

          <div
            
          >
<CardPicker
  user={user}
  onClose={onClose}
  onNavigate={onNavigate}
  openedVia="arena"
  onSelected={(params) => {
    onSelectDone?.(params);
    onClose?.();
  }}
/>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

function ArenasHubModal({
  open,
  onClose,
  children,
}) {
  return (
    <AnimatePresence>
      {open && (
        <div className="modal_box">
            <div className="modal_title">
              Rise Throught The Arenas
              <button onClick={onClose} aria-label="Close" className="modal_close"></button>
            </div>
            <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
              {children}
            </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function findArenaIndexForTrophies(list, trophies) {
  const t = toNum(trophies, 0);

  const idx = list.findIndex((a) => {
    const min = toNum(a?.trophyMin, -Infinity);
    const max = toNum(a?.trophyMax, Infinity);
    return t >= min && t <= max;
  });

  if (idx >= 0) return idx;
  return Math.max(0, (list?.length ?? 1) - 1);
}

                              function ArenaQuickAction({
  icon,
  label,
  value,
  onClick,
  showLiveDot = false,
  topLabel = null,
  boxed = false,
  enabled = true,
  delay = 0,
  enterFrom = "left",
}) {
  return (
    <motion.button
  type="button"
  initial={{
  opacity: 0,
  x: enterFrom === "right" ? 14 : -14,
  scale: 0.9,
}}
  animate={{ opacity: 1, x: 0, scale: 1 }}
  transition={{
    duration: 0.28,
    ease: "easeOut",
    delay,
  }}
  whileTap={enabled ? { scale: 0.92 } : undefined}
onClick={(e) => {
  e.preventDefault();
  e.stopPropagation();

  if (!enabled) return;

  onClick?.();
}}
      aria-label={label}
      title={label}
      style={{
        position: "relative",
        width: 58,
        height: 58,
        padding: 0,
        border: 0,
        background: "transparent",
        cursor: enabled ? "pointer" : "default",
        opacity: enabled ? 1 : 0.55,
        font: "inherit",
        flexShrink: 0,
        overflow: "visible",
      }}
    >
      {topLabel && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: -15,
            transform: "translateX(-50%)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            fontFamily: '"Fredoka", sans-serif',
            whiteSpace: "nowrap",
            textShadow: `
              -1px -1px 0 #000,
               1px -1px 0 #000,
              -1px  1px 0 #000,
               1px  1px 0 #000,
               0px  2px 2px rgba(0,0,0,0.7)
            `,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {topLabel}
        </div>
      )}

      <div
        style={{
          width: 58,
          height: 58,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",

          ...(boxed
            ? {
                background:
                  "linear-gradient(180deg, rgba(122,67,31,0.96) 0%, rgba(74,32,12,0.96) 100%)",
                border: "2px solid #d89442",
                borderRadius: 12,
                boxSizing: "border-box",
                boxShadow: "0 4px 8px rgba(0,0,0,0.35)",
              }
            : {}),
        }}
      >
        <img
          src={icon}
          alt=""
          draggable={false}
          style={{
            width: boxed ? 40 : 58,
            height: boxed ? 40 : 58,
            objectFit: "contain",
            display: "block",
            filter: enabled
  ? "drop-shadow(0 3px 3px rgba(0,0,0,0.55))"
  : "grayscale(100%) brightness(0.7) opacity(0.7)",
            pointerEvents: "none",
          }}
        />

        {showLiveDot && enabled && (
          <span
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#ff0000",
              border: "2px solid #fff",
              boxSizing: "border-box",
              boxShadow:
                "0 0 8px rgba(255,0,0,1), 0 0 14px rgba(255,0,0,0.85)",
              animation:
                "gamesLiveDotPulse 0.9s ease-in-out infinite",
              zIndex: 5,
            }}
          />
        )}

        {value !== undefined && value !== null && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: -12,
              transform: "translateX(-50%)",
              minWidth: 34,
              height: 20,
              padding: "0 3px",
              boxSizing: "border-box",
              borderRadius: 10,
              background: "#1a0101",
              border: "2px solid #d89442",
              color: "#ffe100",
              fontSize: 12,
              lineHeight: "16px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              textAlign: "center",
              textShadow: "1px 1px #000",
              zIndex: 5,
            }}
          >
            {formatCurrencyShort(value)}
          </div>
        )}
      </div>
    </motion.button>
  );
}

            
const HALLOWEEN_BATS = Array.from({ length: 11 }, (_, i) => ({
  id: i,
  top: 8 + ((i * 17) % 72),
  size: 22 + ((i * 13) % 27),
  delay: i * 0.15,
  duration: 2.5 + ((i * 7) % 6) * 0.25,
  drift: (i % 2 === 0 ? -1 : 1) * (12 + i * 3),
}));

function HalloweenBats() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 800,
      }}
    >
      {HALLOWEEN_BATS.map((bat) => (
        <motion.div
          key={bat.id}
          initial={{
            x: "-15vw",
            y: 0,
            opacity: 0,
            scale: 0.6,
          }}
          animate={{
            x: ["-15vw", "40vw", "115vw"],
            y: [0, bat.drift, bat.drift * -0.5],
            opacity: [0, 1, 1, 0],
            scale: [0.6, 1, 0.85],
          }}
          transition={{
            duration: bat.duration,
            delay: bat.delay,
            ease: "linear",
            times: [0, 0.25, 0.75, 1],
          }}
          style={{
            position: "absolute",
            top: `${bat.top}%`,
            left: 0,
            width: bat.size,
            height: bat.size,
            filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))",
          }}
        >
          <motion.svg
            viewBox="0 0 64 40"
            width="100%"
            height="100%"
            animate={{
              scaleY: [1, 0.35, 1, 0.55, 1],
            }}
            transition={{
              duration: 0.35 + (bat.id % 3) * 0.07,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <path
              d="M32 13
                 L27 7 L25 14
                 Q17 4 2 7
                 Q10 13 7 23
                 Q15 20 18 29
                 Q24 23 29 30
                 Q32 34 35 30
                 Q40 23 46 29
                 Q49 20 57 23
                 Q54 13 62 7
                 Q47 4 39 14
                 L37 7 Z"
              fill="#10080e"
              stroke="#2d1928"
              strokeWidth="0.8"
            />
            <circle cx="28" cy="20" r="1" fill="#ff6a00" />
            <circle cx="36" cy="20" r="1" fill="#ff6a00" />
          </motion.svg>
        </motion.div>
      ))}
    </div>
  );
}


function Arenas({
  onNavigate,
  onBack,
  onOpenProfile,
  setUser,
  user,
  hasBattlePass = false,
  battlePassLoading = false,
  selectedCardId,
  arenaIndex: initialArenaIndex,
  arenaId: initialArenaId,
  openedVia,
  viewOnly: initialViewOnly = false,
  tournamentEnabled = false,
  tournamentBadgeOn = false,
  hasTournament = false,
  tournamentStartDate = null,
  tournamentEndDate = null,
  activeTournamentId = null,
  holidayEnabled = false,
  runeHuntersEnabled = false,
  activeEvent = null,
  eventStatusLoaded = false,
            activeThematicEvent = null,
thematicEventStatusLoaded = false,
  setShowSeasonChange,
}) {
  const { arenas, loaded, season, shop, hiddenSeek } = useAppConfig();

 const [pendingRedirect, setPendingRedirect] = useState(null);
const [arenaHubOpen, setArenaHubOpen] = useState(false);
const [viewOnly, setViewOnly] = useState(Boolean(initialViewOnly));
const [fightPickerOpen, setFightPickerOpen] = useState(false);
const [showMyTapisModal, setShowMyTapisModal] = useState(false);

            const [fightRefillOpen, setFightRefillOpen] = useState(false);
const [fightRefillLoading, setFightRefillLoading] = useState(false);
const [fightRefillError, setFightRefillError] = useState("");
const fightRefillAdControllerRef = useRef(null);
            const [fightRefillInfo, setFightRefillInfo] = useState(null);

  useEffect(() => {
    setViewOnly(Boolean(initialViewOnly));
  }, [initialViewOnly]);

  const [defenseDeckRequiredOpen, setDefenseDeckRequiredOpen] = useState(false);

  const chestSpeedupByType = arenas?.chestSpeedupByType || {
    wood: { runes: 0, sparks: 0, time: 0 },
    silver: { runes: 0, sparks: 0, time: 0 },
    golden: { runes: 0, sparks: 0, time: 0 },
    diamond: { runes: 0, sparks: 0, time: 0 },
  };

  const [me, setMe] = useState(user ?? null);

  const hasGamepass = Boolean(me?.gamepass_active ?? user?.gamepass_active);
  const [arenaState, setArenaState] = useState(null);
  const airdrop = Boolean(me?.hasUnclaimedAirdrop || user?.hasUnclaimedAirdrop);
  const hiddenSeekEnabled = Boolean(hiddenSeek?.enabled);
  const hasTapi = Number(user?.tapi ?? 0) > 0;

  const [speedupModalOpen, setSpeedupModalOpen] = useState(false);
  const [speedupTargetChest, setSpeedupTargetChest] = useState(null);
  const [speedupLoading, setSpeedupLoading] = useState(false);
  const [speedupError, setSpeedupError] = useState("");
  const [speedupSuccess, setSpeedupSuccess] = useState("");

  const isTournamentActive = Boolean(
  tournamentEnabled ||
  tournamentBadgeOn ||
  hasTournament
);

  const seasonNumber = useMemo(() => {
  if (!season) return null;

  const candidates = [
    season.number,
    season.season_number,
    season.SEASON_NUMBER,
    season.seasonNumber,
    season.id,          // sometimes season is stored as "id"
    season,             // sometimes it could be just a number
  ];

  const v = candidates.find(x =>
    x !== undefined && x !== null && String(x).trim() !== ""
  );

  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}, [season]);

  useEffect(() => {
    if (user) setMe(user);
  }, [user]);


  async function fetchArenaState() {
  const token = localStorage.getItem("token");

  const res = await fetch(`${API_BASE}/api/arenas/me`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Failed to load arena state");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }
  return data;
}

const arenaReqRef = useRef({ inFlight: false, lastAt: 0 });

const refreshArenaState = useCallback(async ({ force = false } = {}) => {
  const ts = Date.now();

  if (arenaReqRef.current.inFlight) return;
  if (!force && ts - arenaReqRef.current.lastAt < 1500) return;

  arenaReqRef.current.inFlight = true;
  arenaReqRef.current.lastAt = ts;

  try {
    const st = await fetchArenaState();
    setArenaState(st);
    return st;
  } catch (e) {
    console.warn("[arenas] fetchArenaState failed:", e?.message || e);
  } finally {
    arenaReqRef.current.inFlight = false;
  }
}, []);


const [collectedChests, setCollectedChests] = useState([]);
const [collectedLoading, setCollectedLoading] = useState(false);

function formatShortDate(ms) {
  if (!ms) return "";
  try {
    const d = new Date(ms);
    return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
  } catch {
    return "";
  }
}

function getChestSpeedupCfg(speedupMap, chestType) {
  const key = String(chestType || "").toLowerCase().trim();
  return speedupMap?.[key] || { runes: 0, sparks: 0, time: 0 };
}

function formatSpeedupTime(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(" ");
}


async function speedupChestById(chestId) {
  const token = localStorage.getItem("token");

  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/api/arenas/chests/speedup`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
      },
      body: JSON.stringify({ chestId }),
    }
  );

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data?.error || "Failed to speed up chest");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }

  return data;
}

function handleLockedChestClick(chest) {
  const chestType = normalizeChestKey(chest?.chestType);
  const cfg = getChestSpeedupCfg(chestSpeedupByType, chestType);
  const payment = getSpeedupPayment(cfg);

  if (!payment.enabled) {
    return;
  }

  setSpeedupError("");
  setSpeedupSuccess("");
  setSpeedupTargetChest(chest);
  setSpeedupModalOpen(true);
}

async function confirmChestSpeedup() {
  if (!speedupTargetChest?.id) return;

  const chestType = normalizeChestKey(
    speedupTargetChest?.chestType ?? speedupTargetChest?.chest_type
  );

  const cfg = getChestSpeedupCfg(chestSpeedupByType, chestType);
  const payment = getSpeedupPayment(cfg);

  // disabled when runes = 0 and sparks = 0
  if (!payment.enabled) {
    setSpeedupModalOpen(false);
    setSpeedupTargetChest(null);
    setSpeedupError("");
    setSpeedupSuccess("");
    return;
  }

  try {
    setSpeedupLoading(true);
    setSpeedupError("");
    setSpeedupSuccess("");

    const data = await speedupChestById(speedupTargetChest.id);

    if (data?.user) {
      setUser?.((prev) => ({
        ...prev,
        runes: data.user.runes ?? prev?.runes ?? 0,
        sparks: data.user.sparks ?? prev?.sparks ?? 0,
        trophies: data.user.trophies ?? prev?.trophies ?? 0,
      }));
    }

const spedUpChest = speedupTargetChest;

// FECHA O MODAL depois de confirmar com sucesso
setSpeedupModalOpen(false);
setSpeedupTargetChest(null);
setSpeedupError("");
setSpeedupSuccess("");

if (viewOnly && arena?.id) {
  // Past/completed arena screen uses collectedChests, NOT chestSlots.
  // Update the chest immediately with the new unlockAt returned by /speedup.
  if (data?.unlockAt && spedUpChest?.id) {
    setCollectedChests((prev) =>
      prev.map((chest) =>
        String(chest?.id) === String(spedUpChest.id)
          ? {
              ...chest,
              unlockAt: data.unlockAt,
              unlock_at: data.unlockAt,
            }
          : chest
      )
    );

    // If you added the local started-state patch earlier, keep it in sync too.
    if (typeof setViewOnlyUnlockAtById === "function") {
      setViewOnlyUnlockAtById((prev) => ({
        ...prev,
        [String(spedUpChest.id)]: data.unlockAt,
      }));
    }

    if (typeof setViewOnlyStartedChestIds === "function") {
      setViewOnlyStartedChestIds((prev) => {
        const next = new Set(prev);
        next.add(String(spedUpChest.id));
        return next;
      });
    }
  }

  // Also reload the past arena chest list from the correct endpoint.
  const rows = await fetchCollectedChests({ arenaId: arena.id });
  setCollectedChests(rows);
} else {
  // Current arena screen uses chestSlots.
  await refreshChests({ force: true });
}

    if (data?.opened) {
      const meta = getChestMetaFromChest(speedupTargetChest);
      const prizes = mapBackendRewardsToPrizes(data?.rewards, data);

      enqueueModal?.({
        type: "CHEST_OPENED",
        payload: { chest: meta, prizes },
      });
    }
  } catch (e) {
  const code = e?.code || e?.message || "";

  let friendlyMessage = "Unable to speed up this chest right now.";

  if (code === "CHEST_NOT_STARTED") {
  friendlyMessage = "Start unlocking this chest first.";
} else if (code === "NOT_ENOUGH_RUNES") {
  friendlyMessage = "You don’t have enough Runes.";
} else if (code === "NOT_ENOUGH_SPARKS") {
    friendlyMessage = "You don’t have enough Sparks.";
  } else if (code === "CHEST_ALREADY_OPENED") {
    friendlyMessage = "This chest has already been opened.";
  } else if (code === "CHEST_NOT_READY") {
    friendlyMessage = "This chest is still unlocking.";
  } else if (code === "CHEST_NOT_FOUND") {
    friendlyMessage = "Chest not found.";
  } else if (code === "INVALID_CHEST_TYPE") {
    friendlyMessage = "This chest cannot be sped up.";
  }

  setSpeedupError(friendlyMessage);
} finally {
    setSpeedupLoading(false);
  }
}

async function fetchCollectedChests({ arenaId }) {
  const token = localStorage.getItem("token");

  const qs = new URLSearchParams({ arenaId: String(arenaId) });

  // IMPORTANT: route is /chests/collected mounted under /api/arenas
  const res = await fetch(`${API_BASE}/api/arenas/chests/collected?${qs}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP_${res.status}`);
    err.code = data?.error;
    err.payload = data;
    throw err;
  }

  const rows = Array.isArray(data?.chests)
  ? data.chests
  : Array.isArray(data?.items)
  ? data.items
  : Array.isArray(data?.rows)
  ? data.rows
  : Array.isArray(data)
  ? data
  : [];

  // normalize backend fields to what your UI expects
  return rows.map((r) => ({
  id: r.id,
  arenaId: r.arena_id,
  chestType: r.chest_type,

  earnedAt: r.earned_at,
  createdAt: r.earned_at,

  openedAt: r.opened_at,
  unlockAt: r.unlock_at,
  status: r.status,
  rewards: r.rewards || {},
}));
}


const mountedAtRef = useRef(Date.now());

  const claimedReqKeyRef = useRef("");

 const readSelectedCardSnapshot = () => {
  try {
    const raw = localStorage.getItem("selectedCardSnapshot");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const handleFightPickerSelected = useCallback((params = {}) => {
  const snapshot = readSelectedCardSnapshot();

  if (snapshot) {
    setSelectedCard(snapshot);
  }

  setFightPickerOpen(false);

  // Optional but useful: keep the selected id stored even if parent route state
  // does not update because we are not navigating anymore.
  if (params?.cardId) {
    localStorage.setItem("selectedCardId", String(params.cardId));
  }
}, []);

const [selectedCard, setSelectedCard] = useState(readSelectedCardSnapshot);
  

    const battlePrice = useMemo(() => {
  if (!selectedCard) return null;

  const toSafeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const rarity = String(selectedCard?.rarity || "").toLowerCase();
  if (!rarity) return null;

  const feeByRarity = {
    common: toSafeNum(shop?.fightCost?.common ?? shop?.fight?.common),
    rare: toSafeNum(shop?.fightCost?.rare ?? shop?.fight?.rare),
    epic: toSafeNum(shop?.fightCost?.epic ?? shop?.fight?.epic),
    legendary: toSafeNum(shop?.fightCost?.legendary ?? shop?.fight?.legendary),
  };

  return feeByRarity[rarity] ?? null;
}, [selectedCard, shop]);

  useEffect(() => {
  try {
    const raw = localStorage.getItem("selectedCardSnapshot");
    const storedId = localStorage.getItem("selectedCardId");

    if (!raw) {
      setSelectedCard(null);
      return;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      setSelectedCard(null);
      return;
    }

    const expectedId = selectedCardId ?? storedId ?? null;

    if (expectedId && String(parsed.id) !== String(expectedId)) {
      setSelectedCard(null);
      return;
    }

    setSelectedCard(parsed);
  } catch {
    setSelectedCard(null);
  }
}, [selectedCardId]);

useEffect(() => {
  if (!loaded || !user?.id || selectedCard?.id) return;

  let cancelled = false;
  let retryTimer;

  const hasSelection = () => {
    try {
      const raw = localStorage.getItem("selectedCardSnapshot");
      return !!(raw && JSON.parse(raw)?.id);
    } catch {
      return false;
    }
  };

  async function selectFirstFighter(attempt = 0) {
    if (cancelled || hasSelection()) return;

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE}/api/shop/mine?limit=100&offset=0&sortKey=power&sortDir=desc`,
        {
          headers: {
            ...(token
              ? { Authorization: `Bearer ${token}` }
              : {}),
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      const cards = Array.isArray(data.cards)
        ? data.cards
        : [];

      const chosen = cards.find((card) =>
        card?.id &&
        !card.for_sale &&
        !card.is_consumed &&
        !card.merged_into_card_id &&
        (
          !seasonNumber ||
          card.season_number == null ||
          Number(card.season_number) === Number(seasonNumber)
        )
      );

      if (cancelled || hasSelection()) return;

      if (chosen) {
        const snapshot = { ...chosen };

        localStorage.setItem(
          "selectedCardId",
          String(chosen.id)
        );

        localStorage.setItem(
          "selectedCardSnapshot",
          JSON.stringify(snapshot)
        );

        setSelectedCard(snapshot);

        console.log(
          "[Arena] Auto-selected fighter:",
          chosen.id
        );

        return;
      }

      console.warn(
        "[Arena] No cards available yet:",
        attempt + 1,
        data
      );
    } catch (error) {
      if (!cancelled) {
        console.error("[Arena] Auto-selection:", error);
      }
    }

    if (!cancelled && attempt < 14 && !hasSelection()) {
      retryTimer = setTimeout(
        () => selectFirstFighter(attempt + 1),
        1000
      );
    }
  }

  selectFirstFighter();

  return () => {
    cancelled = true;
    clearTimeout(retryTimer);
  };
}, [loaded, user?.id, seasonNumber, selectedCard?.id]);
        
const MAX_CARD_NAME_LEN = 10;


  const truncateCardName = (name) => {
    const n = String(name || "");
    if (n.length <= MAX_CARD_NAME_LEN) return n;
    return n.slice(0, MAX_CARD_NAME_LEN - 1) + "…";
  };

  const [battleStarting, setBattleStarting] = useState(false);
  const [battleCinematic, setBattleCinematic] = useState(false);

  const [allArenasCompleted, setAllArenasCompleted] = useState(false);

  const [chestSlots, setChestSlots] = useState([null, null, null, null]);
  const [unlockingChestId, setUnlockingChestId] = useState(null);
  const [openingChestId, setOpeningChestId] = useState(null);

  const [wins, setWins] = useState(0);
  const trophies = Number(me?.trophies ?? user?.trophies ?? 0);

  const [modalQueue, setModalQueue] = useState([]);
  const [activeModal, setActiveModal] = useState(null);

  const [claimedPrizeIdx, setClaimedPrizeIdx] = useState(() => new Set());

  const enqueueModal = useCallback((item) => {
    setModalQueue((q) => [...q, item]);
  }, []);

  useEffect(() => {
    if (!activeModal && modalQueue.length > 0) {
      setActiveModal(modalQueue[0]);
      setModalQueue((q) => q.slice(1));
    }
  }, [modalQueue, activeModal]);

const closeActiveModal = useCallback(() => {
  setActiveModal(null);

  if (pendingRedirect) {
    const { screen, params } = pendingRedirect;
    setPendingRedirect(null);
    onNavigate?.(screen, params);
  }
}, [pendingRedirect, onNavigate]);

  
  const [arenaIndex, setArenaIndex] = useState(() =>
    Number.isInteger(initialArenaIndex) ? initialArenaIndex : 0
  );

  const arenasList = Array.isArray(arenas?.arenas) ? arenas.arenas : Array.isArray(arenas) ? arenas : [];

        useEffect(() => {
  if (!arenasList.length) return;

  const lastArena = arenasList[arenasList.length - 1];
  const lastMax = toNum(lastArena?.trophyMax, Infinity);

  setAllArenasCompleted(trophies >= lastMax);
}, [trophies, arenasList]);

        useEffect(() => {
  if (!loaded) return;
  if (!Number.isInteger(initialArenaIndex)) return;
  if (!Array.isArray(arenasList) || arenasList.length === 0) return;

  const clamped = Math.max(0, Math.min(initialArenaIndex, arenasList.length - 1));
  setArenaIndex(clamped);
}, [loaded, initialArenaIndex, arenasList]);

  const arena = arenasList?.[arenaIndex] ?? null;
  const title = arena?.name ?? "Arena";

    const openArenasHubModal = useCallback(() => {
  setArenaHubOpen(true);
}, []);

const handleArenasHubNavigate = useCallback(
  (screen, params = {}) => {
    if (screen === "arenas") {
      suppressAutoSnapRef.current = true;

      if (params?.arenaId) {
        const nextIndex = arenasList.findIndex(
          (a) => String(a?.id) === String(params.arenaId)
        );

        if (nextIndex >= 0) {
          setArenaIndex(nextIndex);
        }
      } else if (Number.isInteger(params?.arenaIndex)) {
        setArenaIndex(
          Math.max(0, Math.min(params.arenaIndex, arenasList.length - 1))
        );
      }

      setViewOnly(Boolean(params?.viewOnly));
      setArenaHubOpen(false);
      return;
    }

    setArenaHubOpen(false);
    onNavigate?.(screen, params);
  },
  [arenasList, onNavigate]
);

  useEffect(() => {
  if (!loaded) return;
  if (!Array.isArray(arenasList) || arenasList.length === 0) return;

  if (suppressAutoSnapRef.current) return;

  // If user entered from hub with a specific arena, don't auto-snap
  if (Number.isInteger(initialArenaIndex)) return;

const effectiveId = arenaState?.effectiveArenaId;

if (effectiveId) {
  const idx = arenasList.findIndex(
    (a) => String(a?.id) === String(effectiveId)
  );

  if (idx >= 0) {
    if (idx !== arenaIndex) {
      setArenaIndex(idx);
    }

    // A arena efetiva/current do usuário nunca deve ficar em viewOnly
    setViewOnly(false);
  }

  return;
}

  // fallback (no arenaState yet)
  const nextIndex = findArenaIndexForTrophies(arenasList, trophies);
  if (nextIndex !== arenaIndex) setArenaIndex(nextIndex);
}, [loaded, arenasList, trophies, arenaIndex, initialArenaIndex, arenaState?.effectiveArenaId]);

const goPickFighter = useCallback(() => {
  setFightPickerOpen(true);
}, []);


const ensureAutoDefenseDeck = useCallback(async () => {
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "X-TG-INITDATA":
      window.Telegram?.WebApp?.initData || "",
  };

  // 1. Buscar o Defense Deck atual e as cartas elegíveis.
  const [deckRes, eligibleRes] = await Promise.all([
    fetch(`${API_BASE}/api/pvp/defense-decks`, {
      headers,
    }),
    fetch(`${API_BASE}/api/pvp/defense-deck/overall/eligible`, {
      headers,
    }),
  ]);

  const deckData = await deckRes.json().catch(() => ({}));
  const eligibleData = await eligibleRes.json().catch(() => ({}));

  if (!deckRes.ok) {
    throw new Error(
      deckData?.error || "Failed to load Defense Deck"
    );
  }

  if (!eligibleRes.ok) {
    throw new Error(
      eligibleData?.error || "Failed to load eligible cards"
    );
  }

  const deckSize = Number(deckData.defenseDeckSize || 3);

  const eligibleCards = Array.isArray(eligibleData.cards)
    ? eligibleData.cards.filter((card) =>
        Number.isInteger(Number(card.id)) &&
        Number(card.id) > 0 &&
        !card.for_sale &&
        !card.is_consumed &&
        !card.merged_into_card_id
      )
    : [];

  const eligibleIds = new Set(
    eligibleCards.map((card) => Number(card.id))
  );

  // 2. Recuperar as cartas configuradas manualmente.
  const currentIds = Array.isArray(
    deckData?.decks?.overall?.cardIds
  )
    ? deckData.decks.overall.cardIds.map(Number)
    : [];

  // Preservar as cartas que continuam elegíveis.
  const selectedIds = [
    ...new Set(
      currentIds.filter((id) => eligibleIds.has(id))
    ),
  ].slice(0, deckSize);

  // 3. Se o deck já estiver completo, não modificar.
  if (
    selectedIds.length === deckSize &&
    currentIds.length === deckSize &&
    currentIds.every((id, i) => id === selectedIds[i])
  ) {
    return selectedIds;
  }

  // 4. Calcular o poder das cartas elegíveis.
  const rarityRank = {
    common: 1,
    rare: 2,
    epic: 3,
    legendary: 4,
  };

  const power = (card) =>
    Number(card.attack || 0) +
    Number(card.defense || 0) +
    Number(card.health || 0) +
    Number(card.level || 1) * 2 +
    (rarityRank[
      String(card.rarity || "").toLowerCase()
    ] || 1) * 5;

  const sortedCards = [...eligibleCards].sort(
    (a, b) =>
      power(b) - power(a) ||
      Number(b.id) - Number(a.id)
  );

  // 5. Preencher as posições restantes.
  for (const card of sortedCards) {
    if (selectedIds.length >= deckSize) break;

    const id = Number(card.id);

    if (!selectedIds.includes(id)) {
      selectedIds.push(id);
    }
  }

  if (!selectedIds.length) {
    throw new Error(
      "No eligible cards available for your Defense Deck."
    );
  }

  // 6. Não salvar caso nada tenha mudado.
  if (
    currentIds.length === selectedIds.length &&
    currentIds.every((id, i) => id === selectedIds[i])
  ) {
    return selectedIds;
  }

  // 7. Salvar automaticamente o Defense Deck.
  const saveRes = await fetch(
    `${API_BASE}/api/pvp/defense-deck/overall`,
    {
      method: "PUT",
      headers,
      body: JSON.stringify({
        cardIds: selectedIds,
      }),
    }
  );

  const saved = await saveRes.json().catch(() => ({}));

  if (!saveRes.ok) {
    throw new Error(
      saved?.error || "Failed to auto-fill Defense Deck"
    );
  }

  console.log(
    "[Arena] Defense Deck auto-filled:",
    selectedIds
  );

  return selectedIds;
}, []);


  function getPrizeIconSrc(prize) {
    const type = prize?.item?.type ?? prize?.type ?? prize?.rewardType ?? prize?.prizeType ?? null;

    switch (type) {
      case "runes":
        return "/runes.png";
      case "sparks":
        return "/sparks.png";
      case "booster":
        return "/energy.png";
      case "seasonal_accessory":
        return "/fragment.png";
      case "ability":
        return "/fightlog.png";
      case "card":
        return "/common.png";
      case "evolve":
        return "/evolveicon.png";
      default:
        return "/pack.png";
    }
  }

  const prizesByWins = useMemo(() => {
    const prizes = arena?.prizes;
    if (!Array.isArray(prizes)) return [];
    return prizes.filter((p) => (p?.wins ?? 0) > 0);
  }, [arena]);

  const prizesByTrophies = useMemo(() => {
    const prizes = arena?.prizes;
    if (!Array.isArray(prizes)) return [];
    return prizes.filter((p) => (p?.trophies ?? 0) > 0);
  }, [arena]);


function getPrizeNameForUser(p, hasGamepass) {
  const name =
    hasGamepass
      ? (p?.gpItem?.name || p?.item?.name)
      : (p?.item?.name);

  return name && String(name).trim() ? name : "Prize";
}

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      if (!viewOnly) {
        setCollectedChests([]);
        return;
      }
      if (!arena?.id) return;

      setCollectedLoading(true);
      const rows = await fetchCollectedChests({ arenaId: arena.id });

      if (!cancelled) setCollectedChests(rows);
    } catch (e) {
      console.warn("[Arenas] failed loading collected chests:", e?.message || e);
      if (!cancelled) setCollectedChests([]);
    } finally {
      if (!cancelled) setCollectedLoading(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [viewOnly, arena?.id]);

  const mapPrizesToRows = useCallback(
    (prizes, metric) => {
      return prizes.map((p, idx) => {
        const required = metric === "wins" ? (p?.wins ?? 0) : (p?.trophies ?? 0);
        const current = metric === "wins" ? wins : trophies;

        const safeReq = Math.max(1, required);
        const currentClamped = Math.min(current, safeReq);
        const prizeIndex = Number.isInteger(p?.prizeIndex) ? p.prizeIndex : idx;
        const completed = claimedPrizeIdx.has(prizeIndex);


        const progressPct = Math.round((currentClamped / safeReq) * 100);
        const finalProgressPct = completed ? 100 : progressPct;
        const prizeName = getPrizeNameForUser(p, hasGamepass);


        return {
          leftIcon: getPrizeIconSrc(p),
          borderColor: completed ? "#5C0101" : "#222",
          panelColor: completed ? "#5C0101" : "#222",
          title: (
            <>
              <span style={{ fontWeight: 600, fontSize: 13, color: "#fff" }}>{completed ? "Reward Claimed: " : `Prize at ${required} ${metric}: `}</span>
              <span style={{ fontWeight: 600, fontSize: 13, color: "#E3C58D" }}>{prizeName}</span>
            </>
          ),
          rightIcon: completed ? "/v2/check.png" : "/v2/lock.png",
          showArrow: false,
          progressLabel: completed ? `${required}/${required}` : `${currentClamped}/${required}`,
          progressPct: finalProgressPct,
        };
      });
    },
    [wins, trophies, claimedPrizeIdx]
  );


          useEffect(() => {
  const blockId =
    process.env.REACT_APP_ADSGRAM_FIGHT_REFILL_BLOCK_ID || "22709";

  if (
    typeof window !== "undefined" &&
    window.Adsgram &&
    blockId &&
    !fightRefillAdControllerRef.current
  ) {
    try {
      fightRefillAdControllerRef.current = window.Adsgram.init({
        blockId,
      });
    } catch (e) {
      console.warn("[Adsgram] Arena fight refill init failed:", e);
    }
  }
}, []);

         const arenaLiveShortcuts = useMemo(() => {
  return [
    {
  key: "holiday",
  label: "Holiday Event",
  icon: "/icon_holiday.png?v1",
  route: "holidayevent",
  hidden: !holidayEnabled,
  enabled: Boolean(holidayEnabled),
},
    //{
    //  key: "tournament",
    //  label: "Tournament",
    //  icon: isTournamentActive
    //    ? "/icon_tournament_on.png?v1"
    //    : "/icon_tournament_off.png?v2",
    //  route: "tournaments",
    //  enabled: Boolean(isTournamentActive),
    //},
          {
  key: "tap-game",
  label: "Tap Game",
  icon: "/tapicon.png?v1",
  route: "home",
  hidden: Boolean(activeThematicEvent) || Boolean(holidayEnabled),
  enabled: true,
},
{
  key: "thematic-event",
  label: "Thematic Event",
  icon: "/theme_2_icon.png",
  route: "thematicEvent",
  hidden: !activeThematicEvent,
  enabled: true,
},
    {
  key: "event",
  label: "Special Event",
  icon: "/event_icon.png?v1",
  route: "events",
  enabled: Boolean(eventStatusLoaded && activeEvent),
},
    // Rune Hunters - Temporariamente escondido
{
  key: "rune-hunters",
  label: "Rune Hunters",
  icon: "/icon_rune.png?v1",
  route: "streakevent",
  hidden: true,
  enabled: Boolean(runeHuntersEnabled),
},

// Hidden Seek - No lugar do Rune Hunters
{
  key: "hidden-seek",
  label: "Hidden Seek",
  icon: "/icon_hidden.png?v1",
  route: "hiddenSeek",
  hidden: !hiddenSeekEnabled,
  enabled: true,
  showLiveDot: false,
},
  ];
}, [
  holidayEnabled,
  isTournamentActive,

  activeEvent,
  eventStatusLoaded,

  activeThematicEvent,
  thematicEventStatusLoaded,

  runeHuntersEnabled,
  hiddenSeekEnabled,
]);

  const rewardsWins = useMemo(() => {
    if (!prizesByWins.length) return [];
    return mapPrizesToRows(prizesByWins, "wins");
  }, [prizesByWins, mapPrizesToRows]);

  const rewardsTrophies = useMemo(() => {
    if (!prizesByTrophies.length) return [];
    return mapPrizesToRows(prizesByTrophies, "trophies");
  }, [prizesByTrophies, mapPrizesToRows]);

  const normalizeSlots = useCallback((arr) => {
    const filled = arr.filter(Boolean);
    while (filled.length < 4) filled.push(null);
    return filled.slice(0, 4);
  }, []);

 const arenaMin = toNum(arena?.trophyMin, 0);
const arenaMax = toNum(arena?.trophyMax, 0); // e.g. 400

const trophyMilestones = useMemo(() => {
  const raw = (prizesByTrophies ?? [])
    .map((p) => toNum(p?.trophies, 0))
    .filter((n) => n > 0);

  // ✅ keep only milestones within this arena bounds
  const bounded = raw.filter((n) => n >= arenaMin && (arenaMax > 0 ? n <= arenaMax : true));

  // ✅ sorted top->bottom (small->big)
  bounded.sort((a, b) => a - b);

  return bounded;
}, [prizesByTrophies, arenaMin, arenaMax]);

// ✅ progress respects arena range (0..400, not 0..500)
const verticalPct = useMemo(() => {
  if (!arenaMax || arenaMax <= arenaMin) return 0;

  const clamped = Math.max(arenaMin, Math.min(trophies, arenaMax));
  const range = arenaMax - arenaMin;
  return ((clamped - arenaMin) / range) * 100;
}, [trophies, arenaMin, arenaMax]);

          async function fetchClaimedPrizes({ seasonNumber, arenaId }) {
  const token = localStorage.getItem("token");
  const qs = new URLSearchParams({
    season: String(seasonNumber),
    arenaId: String(arenaId),
  });

  const res = await fetch(`${API_BASE}/api/arenas/prizes/claimed?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);

  // Backend returns: { claimedPrizeIndexes: [...], claimedTrophyMilestones: [...] }
  const arr = Array.isArray(data?.claimedPrizeIndexes) ? data.claimedPrizeIndexes : [];
  return arr.map(Number).filter((x) => Number.isInteger(x) && x >= 0);
}

  const [showTrophySheet, setShowTrophySheet] = useState(false);

const openTrophySheet = useCallback(() => {
  if (rewardsTrophies.length > 0) setShowTrophySheet(true);
}, [rewardsTrophies.length]);

const closeTrophySheet = useCallback(() => setShowTrophySheet(false), []);


const [unopenedOtherArenas, setUnopenedOtherArenas] = useState(0);
const [unopenedOtherArenaId, setUnopenedOtherArenaId] = useState(null);

        async function fetchChestQueue() {
  const token = localStorage.getItem("token");
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/chests`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Failed to load chests");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }
  return data;
}

      const prevUsedRef = useRef(null);
const suppressAutoSnapRef = useRef(false);
const chestReqRef = useRef({ inFlight: false, lastAt: 0 });

const refreshChests = useCallback(async ({ force = false } = {}) => {
  const ts = Date.now();

  if (chestReqRef.current.inFlight) return;
  if (!force && ts - chestReqRef.current.lastAt < 1500) return;

  chestReqRef.current.inFlight = true;
  chestReqRef.current.lastAt = ts;

  try {
    const data = await fetchChestQueue();

    setChestSlots(data?.slots || [null, null, null, null]);
    setUnopenedOtherArenas(Number(data?.unopenedOtherArenas ?? 0));
    setUnopenedOtherArenaId(data?.unopenedOtherArenaId ?? null);

    const used = Number(data?.usedToday ?? 0);
    if (prevUsedRef.current != null && used > prevUsedRef.current) {
      const newest = (data?.slots || []).filter(Boolean).slice(-1)[0];
      if (newest?.chestType) {
        enqueueModal?.({
          type: "CHEST_WON",
          payload: newest,
        });
      }
    }

    prevUsedRef.current = used;
    return data;
  } finally {
    chestReqRef.current.inFlight = false;
  }
}, [enqueueModal]);


useEffect(() => {
  if (!loaded) return;

  refreshArenaState({ force: true });

  if (!viewOnly) {
    refreshChests({ force: true }).catch(() => {});
  }
}, [loaded, viewOnly, refreshArenaState, refreshChests]);


useEffect(() => {
  const shouldIgnoreInitialBurst = () => {
    return Date.now() - mountedAtRef.current < 1500;
  };

  const onFocus = () => {
    if (shouldIgnoreInitialBurst()) return;

    refreshArenaState();
    if (!viewOnly) {
      refreshChests().catch(() => {});
    }
  };

  const onVis = () => {
    if (document.visibilityState !== "visible") return;
    if (shouldIgnoreInitialBurst()) return;

    refreshArenaState();
    if (!viewOnly) {
      refreshChests().catch(() => {});
    }
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVis);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVis);
  };
}, [refreshArenaState, refreshChests, viewOnly]);

async function startChestUnlockById(chestId) {
  const token = localStorage.getItem("token");

  const res = await fetch(`${API_BASE}/api/arenas/chests/start-unlock`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    },
    body: JSON.stringify({ chestId }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data?.error || "Failed to start chest unlock");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }

  return data;
}

async function openChestById(chestId) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/chests/open-guaranteed-sparks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    },
    body: JSON.stringify({ chestId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || "Failed to open chest");
    err.code = data?.error;
    err.payload = data;
    throw err;
  }
  return data;
}


  async function startBattle({ cardId, arenaId }) {
    const token = localStorage.getItem("token");

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/battle/bf8c3a/starta`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
      },
      body: JSON.stringify({ cardId, arenaId }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const err = new Error(data?.error || "Failed to start battle");
      err.code = data?.error;
      err.payload = data;
      throw err;
    }

    return data;
  }




       useEffect(() => {
  let cancelled = false;

  if (!loaded) return;
  if (!seasonNumber) return;
  if (!arena?.id) return;

  const reqKey = `${seasonNumber}__${arena.id}`;
  if (claimedReqKeyRef.current === reqKey) return;
  claimedReqKeyRef.current = reqKey;

  (async () => {
    try {
      const indexes = await fetchClaimedPrizes({
        seasonNumber,
        arenaId: arena.id,
      });

      if (!cancelled) {
        setClaimedPrizeIdx(new Set(indexes));
      }
    } catch (e) {
      console.warn("[Arenas] failed loading claimed prizes:", e?.message || e);
      if (!cancelled) {
        setClaimedPrizeIdx(new Set());
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [loaded, seasonNumber, arena?.id]);

  const openDefenseDeckRequiredModal = useCallback(() => {
  setBattleCinematic(false);
  setBattleStarting(false);
  setDefenseDeckRequiredOpen(true);
}, []);

const goToDefenseDeck = useCallback(() => {
  setDefenseDeckRequiredOpen(false);

  onNavigate?.("defenseDeck", {
    openedVia: "arena",
    returnTo: "arenas",
    arenaId: arena?.id,
    arenaIndex,
  });
}, [
  onNavigate,
  arena?.id,
  arenaIndex,
]);

const loadFightRefillInfo = async () => {
  try {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE}/api/battle/fights-refill-video/status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json().catch(() => ({}));

    if (res.ok && data?.ok) {
      setFightRefillInfo(data);
    }
  } catch (e) {
    console.warn("[Arena] refill status failed:", e);
  }
};

  const handleFightRefillVideo = async () => {
  if (fightRefillLoading) return;

  const cardId = selectedCard?.id;

  if (!cardId) {
    setFightRefillError("No fighter selected.");
    return;
  }

  if (!fightRefillAdControllerRef.current) {
    setFightRefillError("Ad service not available. Please try again.");
    return;
  }

  setFightRefillError("");
  setFightRefillLoading(true);

  try {
    // 1. User watches the rewarded ad
    await fightRefillAdControllerRef.current.show();

    // 2. Tell backend to add +1 fight to THIS card
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/battle/fights-refill-video`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cardId,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.ok) {
      setFightRefillError(
        data?.error || "Could not unlock another battle."
      );
      return;
    }

    // 3. Close refill modal
    setFightRefillOpen(false);
    setFightRefillError("");

    // 4. Tell the user it worked
    enqueueModal({
      type: "INFO",
      payload: {
        message: "1 extra battle unlocked for this card!",
      },
    });
  } catch (error) {
  console.warn("[Adsgram] Arena fight refill:", error);

  if (error?.error) {
    setFightRefillError("Ad failed to load. Please try again.");
  } else {
    setFightRefillError("Ad was skipped or closed.");
  }
} finally {
    setFightRefillLoading(false);
  }
};


  const handleBattleClick = useCallback(async () => {
    if (battleStarting) return;

        const arenaMax = toNum(arena?.trophyMax, Infinity);

    const isLastArena =
    arenasList.length > 0 &&
    arenaIndex === arenasList.length - 1;

  const completedFinalArena =
    isLastArena &&
    trophies >= arenaMax;

if (trophies > arenaMax && !completedFinalArena) {
  enqueueModal?.({
    type: "INFO",
    payload: { message: "This arena is completed. Battles are disabled." },
  });
  return;
}

      const isCurrentArena =
  arenaState?.effectiveArenaId &&
  String(arena?.id) === String(arenaState.effectiveArenaId);

if (viewOnly && !isCurrentArena && !completedFinalArena) {
  enqueueModal?.({
    type: "INFO",
    payload: {
      message:
        "This arena is completed. You can view prizes here, but battles are disabled.",
    },
  });
  return;
}

    const cardId = selectedCard?.id || null;
    const arenaId = arena?.id;

    if (!arenaId) {
      enqueueModal({ type: "INFO", payload: { message: "Arena not ready yet." } });
      return;
    }

   
try {
  setBattleStarting(true);
  setBattleCinematic(true);

  // AUTO FILL DO DEFENSE DECK
  await ensureAutoDefenseDeck();

  // INICIAR BATALHA NORMALMENTE
  const [, data] = await Promise.all([
    sleep(BATTLE_CINEMATIC_MS),
    startBattle({ cardId, arenaId }),
  ]);


      if (!data?.battleId) throw new Error("Missing battleId from server.");

      onNavigate("battle", {
  battleId: data.battleId,
  cardId: data.cardId || cardId,
  arenaId,
        returnTo: "arenas",
        returnParams: { arenaId, fromArenaIndex: arenaIndex, openedVia: "battle" },
      });
    } catch (e) {
      console.warn("[arenas] battle failed:", {
        message: e?.message,
        cardId: selectedCardId,
        arenaId: arena?.id,
        err: e,
      });


  const errorCode =
  String(
    e?.code ||
    e?.message ||
    ""
  )
    .trim()
    .toUpperCase();

if (errorCode === "CARD_NOT_FOUND") {
  localStorage.removeItem("selectedCardId");
  localStorage.removeItem("selectedCardSnapshot");

  setSelectedCard(null);

  setBattleCinematic(false);

  goPickFighter();

  return;
}

  if (e?.code === "DEFENSE_DECK_REQUIRED" || e?.message === "DEFENSE_DECK_REQUIRED") {
  openDefenseDeckRequiredModal();
  return;
}

if (
  e?.code === "DAILY_LIMIT" ||
  e?.code === "erro_limit" ||
  e?.code === "ERROR_LIMIT" ||
  e?.message === "DAILY_LIMIT"
) {
  setBattleCinematic(false);
  setFightRefillError("");
  setFightRefillInfo(null);
  setFightRefillOpen(true);

  loadFightRefillInfo();

  return;
}

        if (e?.code === "ARENA_LOCKED") {
          const effectiveId =
  e?.payload?.effectiveArenaId ||
  e?.payload?.effective_arena_id ||
  e?.effectiveArenaId ||
  e?.effective_arena_id;

          if (effectiveId) {
            const idx = arenasList.findIndex(a => String(a?.id) === String(effectiveId));
            if (idx >= 0) setArenaIndex(idx);
          } else {
            // fallback: re-fetch arenaState and use it
            try {
              const st = await fetchArenaState();
              setArenaState(st);
              const idx = arenasList.findIndex(a => String(a?.id) === String(st?.effectiveArenaId));
              if (idx >= 0) setArenaIndex(idx);
            } catch {}
          }

          enqueueModal({
            type: "INFO",
            payload: { message: "You are protected in a higher arena. Redirected to your current arena." },
          });
          return;
        }

if (e?.code === "ARENA_ENTRY_REQUIRED") {
  enqueueModal({
    type: "INFO",
    payload: {
      message: "This arena requires an entry purchase. Opening the Arenas Hub.",
    },
  });

  setArenaHubOpen(true);
  return;
}

      if (e?.code === "ARENA_TROPHY_LOCKED") {
        const nextIndex = findArenaIndexForTrophies(arenasList, trophies);
        setArenaIndex(nextIndex);

        enqueueModal({
          type: "INFO",
          payload: { message: "You advanced to the correct arena for your trophies." },
        });
        return;
      }

      enqueueModal({
        type: "INFO",
        payload: { message: e?.message || "Failed to start battle" },
      });

      setBattleCinematic(false);
    } finally {
      setBattleStarting(false);
      setTimeout(() => setBattleCinematic(false), 80);
    }
  }, [
  battleStarting,
  selectedCardId,
  arena?.id,
  arena?.trophyMax,
  viewOnly,
  arenaState?.effectiveArenaId,
  enqueueModal,
  onNavigate,
  arenasList,
  trophies,
  goPickFighter,
  arenaIndex,
  fetchArenaState,
  ensureAutoDefenseDeck,
]);

const applyUserFromPayload = useCallback((payload) => {
  const patch = payload?.user ?? payload?.updatedUser ?? null;

  if (!patch || typeof patch !== "object") return;

  setMe((prev) => ({
    ...(prev || user || {}),
    ...(patch.sparks !== undefined ? { sparks: patch.sparks } : {}),
    ...(patch.runes !== undefined ? { runes: patch.runes } : {}),
    ...(patch.trophies !== undefined ? { trophies: patch.trophies } : {}),
  }));

  setUser?.((prev) => ({
    ...(prev || user || {}),
    ...(patch.sparks !== undefined ? { sparks: patch.sparks } : {}),
    ...(patch.runes !== undefined ? { runes: patch.runes } : {}),
    ...(patch.trophies !== undefined ? { trophies: patch.trophies } : {}),
  }));
}, [setUser, user]);

const handleChestClick = useCallback(
  async (clickedIndex) => {
    const normalized = normalizeSlots(chestSlots);

    const slot = normalized[clickedIndex];
    if (!slot?.id) {
      enqueueModal({ type: "INFO", payload: { message: "Empty slot." } });
      return;
    }

    const notStarted = isChestUnlockNotStartedClient(slot);

    const unlockAtMs = toMsTimestamp(
      slot.unlockAt ?? slot.unlock_at ?? slot.unlockAtMs ?? slot.unlock_at_ms,
      0
    );

    const remainingMs = unlockAtMs - Date.now();

   if (notStarted) {
  if (unlockingChestId) return;

  try {
    setUnlockingChestId(String(slot.id));

    await startChestUnlockById(slot.id);
    await refreshChests({ force: true });
  } catch (e) {
    enqueueModal({
      type: "INFO",
      payload: {
        message: e?.message || "Failed to start chest unlock",
      },
    });
  } finally {
    setUnlockingChestId(null);
  }

  return;
}

    if (remainingMs > 0) {
      handleLockedChestClick(slot);
      return;
    }

   try {
  setOpeningChestId(String(slot.id));

  const opened = await openChestById(slot.id);
  const prizes = mapBackendRewardsToPrizes(opened?.rewards, opened);

  applyUserFromPayload(opened);
  await refreshChests({ force: true });

  enqueueModal({
    type: "CHEST_OPENED",
    payload: {
      chest: getChestMetaFromChest(slot),
      prizes,
    },
  });
} catch (e) {
  enqueueModal({
    type: "INFO",
    payload: {
      message: e?.message || "Failed to open chest",
    },
  });
} finally {
  setOpeningChestId(null);
}
  },
  [
    chestSlots,
    normalizeSlots,
    enqueueModal,
    refreshChests,
    applyUserFromPayload,
  ]
);

  const [arenaTab, setArenaTab] = useState("free"); // default: free

  // Heuristic paid detection (safe if config differs)
  const isPaidArena = useCallback((a) => {
    if (!a) return false;
    return Boolean(
      a.isPaid ||
        a.paid ||
        a.type === "paid" ||
        a.access === "paid" ||
        (Number(a.entryFee ?? 0) > 0) ||
        (Number(a.entryCost ?? 0) > 0)
    );
  }, []);

  // “Enter only the arena he has enough trophies to play”
  // -> keep same rule as your auto-arena: trophies must be within [min,max]
  const canEnterArena = useCallback(
    (a) => {
      const min = toNum(a?.trophyMin, -Infinity);
      const max = toNum(a?.trophyMax, Infinity);
      return trophies >= min && trophies <= max;
    },
    [trophies]
  );

  const neededForMin = useCallback(
    (a) => {
      const min = toNum(a?.trophyMin, 0);
      return Math.max(0, min - trophies);
    },
    [trophies]
  );

  const arenasForTab = useMemo(() => {
    const list = Array.isArray(arenasList) ? arenasList : [];
    return list.filter((a) => (arenaTab === "paid" ? isPaidArena(a) : !isPaidArena(a)));
  }, [arenasList, arenaTab, isPaidArena]);

 const normalizedSlots = useMemo(() => {
  return normalizeSlots(chestSlots);
}, [normalizeSlots, chestSlots]);

const sortedChestSlots = useMemo(() => {
  const now = Date.now();

  const getPriority = (slot) => {
    if (!slot) return 4;

    const notStarted = isChestUnlockNotStartedClient(slot);

    const unlockAtMs = toMsTimestamp(
      slot.unlockAt ?? slot.unlock_at ?? slot.unlockAtMs ?? slot.unlock_at_ms,
      0
    );

    const remainingMs = unlockAtMs - now;

    if (!notStarted && remainingMs <= 0) return 0; // OPEN
    if (notStarted) return 1;                      // START
    if (remainingMs > 0) return 2;                  // countdown
    return 3;
  };

  return normalizedSlots
    .map((slot, originalIndex) => ({
      slot,
      originalIndex,
      priority: getPriority(slot),
    }))
    .sort((a, b) => a.priority - b.priority);
}, [normalizedSlots]);

const sortedCollectedChests = useMemo(() => {
  const now = Date.now();

  const getPriority = (chest) => {
    if (!chest) return 3;

    const unlockAtMs = toMsTimestamp(
      chest.unlockAt ?? chest.unlock_at ?? chest.unlockAtMs ?? chest.unlock_at_ms,
      0
    );

    const notStarted = isChestUnlockNotStartedClient(chest);
    const isOpened = Boolean(chest.openedAt ?? chest.opened_at);
    const remainingMs = unlockAtMs - now;

    if (!isOpened && !notStarted && remainingMs <= 0) return 0; // OPEN
    if (!isOpened && notStarted) return 1;                      // UNLOCK
    if (!isOpened && remainingMs > 0) return 2;                  // countdown
    return 3;                                                    // claimed/opened
  };

  return [...collectedChests].sort((a, b) => {
    return getPriority(a) - getPriority(b);
  });
}, [collectedChests]);



const DEBUG_MODALS = false;

const debugPrizePayload = {
  chest: {
    key: "wood",
    label: "WOOD",
    image: "v2/chest1.png",
  },
  prizes: [
    {
      id: "debug_sparks",
      type: "sparks",
      label: "Sparks",
      icon: "/sparks.png",
      amount: 150,
      quality: "common",
    },
    {
      id: "debug_runes",
      type: "runes",
      label: "Runes",
      icon: "/runes.png",
      amount: 5,
      quality: "rare",
    },
    {
      id: "debug_cardpieces",
      type: "cardPieces",
      label: "Card Pieces",
      icon: "/pieces.png",
      amount: 20,
      quality: "common",
      cardName: "Debug Fighter",
    },
  ],
};

/* =========================
   Render
   ========================= */

if (!loaded || !arena) {
  return (
    <div
      className="shop-container"
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoadingSpinner text="Loading Arenas" size={64} />
    </div>
  );
}

const filledIndexes = sortedChestSlots
  .map((entry, idx) => (entry.slot ? idx : null))
  .filter((x) => x != null);

const oldestIndex = filledIndexes.length > 0 ? filledIndexes[0] : -1;

  const isFinalArena =
  arenasList.length > 0 &&
  arenaIndex === arenasList.length - 1;

const finalArenaCompleted =
  isFinalArena &&
  trophies >= toNum(arena?.trophyMax, Infinity);
const isCurrentArena =
  arenaState?.effectiveArenaId &&
  String(arena?.id) === String(arenaState.effectiveArenaId);

const battleButtonDisabled =
  viewOnly &&
  !isCurrentArena &&
  !finalArenaCompleted;

return (
  <div className="shop-container" style={{overflowX: "hidden" }}>

    {/* TOP BAR */}
    <GamesTopBar
  user={me ?? user}
  onNavigate={onNavigate}
  openedVia="arenas"
  onOpenProfile={onOpenProfile}
  passType="battlepass"
  hasBattlePass={hasBattlePass}
  battlePassLoading={battlePassLoading}

  leaderboardParams={{
    arenaIndex,
    arenaId: arena?.id,
    viewOnly,
  }}
/>
  
<div
  style={{
    position: "absolute",
    left: 5,
    right: 5,
    top: 64,
    bottom: 95,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  }}
>

      {/* =========================
         HERO ARENA + BOTTOM FIGHTER BAR (unchanged)
         ========================= */}

     <div
  style={{
    width: "100%",
    position: "relative",
  }}
>
  {/* EVENTOS - NÃO RECEBEM O ZOOM */}
  {arenaLiveShortcuts.length > 0 && (
    <div
      style={{
        position: "absolute",
        left: 4,
        top: "50%",
        transform: "translateY(-42%)",
        zIndex: 650,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        pointerEvents: "auto",
      }}
    >
{arenaLiveShortcuts.map((shortcut, index) => {
  if (shortcut.hidden) return null;

  return (
    <ArenaQuickAction
      key={shortcut.key}
      icon={shortcut.icon}
      label={shortcut.label}
      enabled={shortcut.enabled}
      delay={index * 0.07}
      showLiveDot={
  shortcut.showLiveDot !== undefined
    ? shortcut.showLiveDot
    : shortcut.enabled && shortcut.key !== "tap-game"
}
      onClick={() => onNavigate?.(shortcut.route)}
    />
  );
})}
    </div>
  )}

  {/* TAPI - NÃO RECEBE O ZOOM */}
 {/* RIGHT SIDE - PERMANENT ACTIONS */}
<div
  style={{
    position: "absolute",
    right: 4,
    top: "50%",
    transform: "translateY(-42%)",
    zIndex: 650,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    pointerEvents: "auto",
  }}
>
  <ArenaQuickAction
    icon="/icodrop.png"
    label="Airdrop"
    enterFrom="right"
    topLabel="AIRDROP"
    boxed={false}
    value={me?.tapi ?? user?.tapi ?? 0}
    onClick={() => setShowMyTapisModal(true)}
  />

  <ArenaQuickAction
    icon="/fortunebt.png?v1"
    enterFrom="right"
    label="Fortune Wheel"
    onClick={() => onNavigate?.("fortuneWheel")}
  />

  <ArenaQuickAction
    enterFrom="right"
    icon="/memory_big.png?v1"
    label="Memory Game"
    onClick={() => onNavigate?.("memoryGame")}
  />
</div>

  {/* A ARENA CONTINUA RECEBENDO O ZOOM */}
  <motion.div
    initial={false}
    animate={battleCinematic ? "go" : "idle"}
    variants={stageVariants}
    onClick={openTrophySheet}
    style={{
      marginTop: "0px",
      width: "100%",
      position: "relative",
      pointerEvents: "auto",
    }}
  >
    <motion.div
      initial={false}
      animate={battleCinematic ? "go" : "idle"}
      variants={arenaPanelVariants}
      style={{
        position: "relative",
        width: "100%",
        cursor: rewardsTrophies.length ? "pointer" : "default",
      }}
    >
      <AnimatePresence>
        {battleCinematic && (
          <motion.div
            key="arena-flash"
            initial="idle"
            animate="go"
            exit="idle"
            variants={flashVariants}
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 60% 50%, rgba(255,223,95,0.65), rgba(255,120,0,0.20), rgba(0,0,0,0) 60%)",
              filter: "blur(1px)",
              mixBlendMode: "screen",
              pointerEvents: "none",
              zIndex: 3,
            }}
          />
        )}
      </AnimatePresence>

          <img
            src={'v2/'+arena?.imageUrl || "./arena.png"}
            alt={arena?.name || "Arena"}
            style={{
              height: "calc(28dvh - var(--footer-bottom-offset))",
              display: "inline-block",
              paddingTop: 60
            }}
          />


          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "40%",
              zIndex: 4,
              pointerEvents: "auto",
            }}
          />

                   <div
            onClick={(e) => {
  e.preventDefault();
  e.stopPropagation();
  openArenasHubModal();
}}
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 600,
              width: 190,
              minHeight: 42,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: "0 10px 0 10px",
              background: "#6f3d1d",
              borderRadius: 6,
              pointerEvents: "auto",
              cursor: "pointer",
              borderRight: "1px solid #56220d",
              borderLeft: "1px solid #56220d",
              borderBottom: "3px solid #56220d",
              fontFamily: '"Fredoka", sans-serif',
              animation:
                !viewOnly &&
                String(arena?.id) === String(arenaState?.effectiveArenaId) &&
                unopenedOtherArenas > 0
                  ? "arenaGlowPulse 1.1s ease-in-out infinite"
                  : "none",
            }}
          >

            {!viewOnly &&
              unopenedOtherArenas > 0 && (
                <div
                  onClick={(e) => {
  e.preventDefault();
  e.stopPropagation();

  if (!unopenedOtherArenaId) return;

  const idx = arenasList.findIndex(
    (a) => String(a?.id) === String(unopenedOtherArenaId)
  );

  if (idx < 0) return;

  suppressAutoSnapRef.current = true;
  setArenaIndex(idx);
  setViewOnly(true);
}}
                  style={{
                    fontSize: 12,
                    lineHeight: "18px",
                    borderRadius: "50%",
                    color: "#fff",
                    position: "absolute",
                    top: -6,
                    right: -6,
                    height: 21,
                    width: 21,
                    backgroundColor: "#fe4300",
                    borderBottom: "1px solid #ca0600",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 600,
                    zIndex: 601,
                    cursor: "pointer",
                  }}
                >
                  {unopenedOtherArenas > 9 ? "9+" : unopenedOtherArenas}
                </div>
              )}


            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                minWidth: 0,
                flex: 1,
              }}
            >
              <div
                style={{
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </div>

              <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
    marginLeft: "auto",
  }}
>
  <img
    src="/v2/trophy.png"
    alt=""
    style={{ width: 23, height: 23, display: "block" }}
  />
  <span style={{ fontWeight: 600, color: "#FFE100" }}>
    {arena?.trophyMax ?? 0}
  </span>
</div>
            </div>
          </div>

            </motion.div>
  </motion.div>
</div>

<div
  style={{
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    gap: 0,
    marginTop: 10,
  }}
>
  {/* BATTLE */}
<button
  type="button"
  onClick={handleBattleClick}
  disabled={battleButtonDisabled}
  className="battle_bt"
  style={{
    marginTop: 0,
    cursor: battleButtonDisabled ? "not-allowed" : "pointer",
    opacity: battleButtonDisabled ? 0.5 : 1,
  }}
>

  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
      lineHeight: 1,
      color: "#fff",
      fontWeight: "bold",
      textShadow: "0 2px 4px rgba(0,0,0,0.6)",
      fontFamily: '"Fredoka", sans-serif',
      marginTop: 24
    }}
  >
    {battlePrice != null && (
  <span
    style={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      fontSize: 14,
    }}
  >
    <img
      src="./sparks.png"
      alt="sparks"
      style={{
        height: 18,
        width: "auto"
      }}
    />
    {battlePrice}
  </span>
)}
  </div>

</button>

  {!viewOnly && (
  <motion.button
    type="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      goPickFighter();
    }}
    aria-label="Change Fighter"
    style={{
      position: "relative",
      width: 54,
      height: 54,
      marginLeft: 10,
      marginTop: 0,
      padding: 0,
      border: "1px solid #f1c46a",
      borderRadius: 6,
      cursor: "pointer",
      overflow: "visible",
      flexShrink: 0,
      background:
        "linear-gradient(145deg, #8d552b 0%, #4b210f 60%, #2b1008 100%)",
      boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.35),
        inset 0 0 7px rgba(255,194,92,0.18),
        0 2px 4px rgba(0,0,0,0.38)
      `,
    }}
  >
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 6,
        overflow: "hidden",
        background:
          "radial-gradient(circle at 50% 35%, #30221d 0%, #090707 70%, #000 100%)",
        border: "1px solid rgba(255,223,154,0.35)",
      }}
    >
      {selectedCard ? (
        <FighterThumb card={selectedCard} />
      ) : (
        <img
          src="/v2/nocard.png"
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: 0.8,
          }}
        />
      )}
    </div>

    <div
      style={{
        position: "absolute",
        right: -4,
        bottom: -4,
        width: 18,
        height: 18,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(180deg, #ffd762 0%, #e58418 100%)",
        border: "1px solid #fff0a6",
        boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
        zIndex: 30,
        pointerEvents: "none",
      }}
    >
      <img
        src="/v2/switch.png"
        alt=""
        style={{
          width: 11,
          height: 11,
          objectFit: "contain",
          filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.45))",
        }}
      />
    </div>
  </motion.button>
)}

  
</div>
</div>

      <div style={{ height: 12 }} />

  {/* Chests */}
<div
  style={{
    position: "absolute",
    left: 5,
    right: 5,
    bottom: 0,
    padding: "0 5px 5px",
    boxSizing: "border-box",
    zIndex: 20,
  }}
>
{viewOnly ? (

  <div
    style={{
      ...chestWrapStyle,
      justifyContent: "flex-start",
      overflowX: "auto",
      overflowY: "visible",
      paddingTop: 12,
      paddingBottom: 12,
      WebkitOverflowScrolling: "touch",
      paddingLeft: 5,
      paddingRight: 5,
      boxSizing: "border-box",
      marginTop: 0,
    }}
  >
   {collectedLoading ? (
  <div
    style={{
      width: "100%",
      minHeight: 120,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 10,
      boxSizing: "border-box",
    }}
  >
    <LoadingSpinner text="Loading Chests" size={44} />
  </div>
) : (
  [...sortedCollectedChests, ...Array(Math.max(0, 4 - sortedCollectedChests.length)).fill(null)]
    .slice(0, 4)
    .map((c, i) => {
      if (!c) return <EmptyChestSlot key={`view_empty_${i}`} />;
  const meta = getChestMetaFromChest(c);

  const unlockAtMs = toMsTimestamp(
  c.unlockAt ?? c.unlock_at ?? c.unlockAtMs ?? c.unlock_at_ms,
  0
);

const remainingMs = unlockAtMs - Date.now();
const notStarted = isChestUnlockNotStartedClient(c);

const isOpened = Boolean(c.openedAt ?? c.opened_at);
const isReady = !isOpened && !notStarted && remainingMs <= 0;
const isLocked = !isOpened && !notStarted && remainingMs > 0;

let badgeText = "CLAIMED";
let showClock = false;
let isClickable = false;

const speedupCfg = getChestSpeedupCfg(
  chestSpeedupByType,
  normalizeChestKey(c?.chestType ?? c?.chest_type)
);

const speedupPayment = getSpeedupPayment(speedupCfg);
const canSpeedup = isLocked && speedupPayment.enabled;

const speedupIcon =
  speedupPayment.currency === "runes"
    ? "/runes.png"
    : speedupPayment.currency === "sparks"
    ? "/sparks.png"
    : null;

const isUnlocking =
  unlockingChestId !== null &&
  String(unlockingChestId) === String(c.id);

const isOpening =
  openingChestId !== null &&
  String(openingChestId) === String(c.id);

if (!isOpened) {

  if (notStarted) {
  badgeText = "UNLOCK";
  showClock = false;
  isClickable = !isUnlocking;
} else if (isLocked) {
    badgeText = canSpeedup ? (
      <>
        {speedupIcon && (
          <img
  src="./v2/clock.png"
  alt=""
  style={{
    width: 16,
    height: 16,
    objectFit: "contain",
    verticalAlign: "middle",
    marginRight: 1,
    transform: "translateY(-1px)",
  }}
/>
        )}
        <ChestCountdown unlockAtMs={unlockAtMs} />
      </>
    ) : (
      <ChestCountdown unlockAtMs={unlockAtMs} />
    );

    showClock = !canSpeedup;
    isClickable = true;
  } else {
  badgeText = "OPEN";
  isClickable = !isOpening;
}
}
        
  return (
    <ChestCard
      key={c.id}
      appearDelay={i * 0.05}
      isProcessing={isUnlocking || isOpening}
      label={isLocked ? "OPEN NOW" : meta.label}
      image={meta.image}
      badgeText={badgeText}
      showClock={showClock}
      isReady={isReady}
      isClickable={isClickable}
      disableGlow={!isReady}
      isStart={notStarted}
      onClick={
  isOpened
    ? undefined
    : async () => {
        if (notStarted) {
  if (unlockingChestId) return;

  try {
    setUnlockingChestId(String(c.id));

    await startChestUnlockById(c.id);

    const rows = await fetchCollectedChests({
      arenaId: arena.id,
    });

    setCollectedChests(rows);
  } catch (e) {
    enqueueModal({
      type: "INFO",
      payload: {
        message: e?.message || "Failed to start chest unlock",
      },
    });
  } finally {
    setUnlockingChestId(null);
  }

  return;
}

        if (isLocked) {
          handleLockedChestClick(c);
          return;
        }

        try {
  setOpeningChestId(String(c.id));

  const opened = await openChestById(c.id);
  const prizes = mapBackendRewardsToPrizes(opened?.rewards, opened);

  applyUserFromPayload(opened);

  const rows = await fetchCollectedChests({
    arenaId: arena.id,
  });

  setCollectedChests(rows);

  enqueueModal({
    type: "CHEST_OPENED",
    payload: {
      chest: meta,
      prizes,
    },
  });
} catch (e) {
  enqueueModal({
    type: "INFO",
    payload: {
      message: e?.message || "Failed to open chest",
    },
  });
} finally {
  setOpeningChestId(null);
}
      }
}
      cardStyle={{
  ...chestSlotFixedStyle,
  ...(canSpeedup
    ? {
        boxShadow:
          "0 0 12px rgba(255, 223, 95, 0.85), 0 0 22px rgba(255, 223, 95, 0.35)",
      }
    : {}),
}}
    />
  );
})
    )}
  </div>
) : (
  // existing normal slots render unchanged
  <div style={chestWrapStyle}>
  {sortedChestSlots.map(({ slot, originalIndex }, i) => {
  if (!slot) {
    return (
      <EmptyChestSlot
        key={`empty_${i}`}
        loading={!loaded}
      />
    );
  }

    const unlockAtMs = toMsTimestamp(
      slot.unlockAt ?? slot.unlock_at ?? slot.unlockAtMs ?? slot.unlock_at_ms,
      0
    );

    const notStarted = isChestUnlockNotStartedClient(slot);
const remainingMs = unlockAtMs - Date.now();

const isReady = !notStarted && remainingMs <= 0;
const isLocked = !notStarted && remainingMs > 0;

const speedupCfg = getChestSpeedupCfg(
  chestSpeedupByType,
  normalizeChestKey(slot?.chestType ?? slot?.chest_type)
);

const speedupPayment = getSpeedupPayment(speedupCfg);
const canSpeedup = isLocked && speedupPayment.enabled;

const speedupIcon =
  speedupPayment.currency === "runes"
    ? "/runes.png"
    : speedupPayment.currency === "sparks"
    ? "/sparks.png"
    : null;

const isUnlocking =
  unlockingChestId !== null &&
  String(unlockingChestId) === String(slot.id);

const isOpening =
  openingChestId !== null &&
  String(openingChestId) === String(slot.id);

const isClickable = !isUnlocking && !isOpening;
const showClock = isLocked && !canSpeedup;

const badgeText = notStarted
  ? "UNLOCK"
  : isReady
  ? "OPEN"
  : canSpeedup
  ? (
      <>
        {speedupIcon && (
          <img
  src="./v2/clock.png"
  alt=""
  style={{
    width: 14,
    height: 14,
    objectFit: "contain",
    verticalAlign: "middle",
    marginRight: 3,
    transform: "translateY(-1px)",
  }}
/>
        )}
        <ChestCountdown unlockAtMs={unlockAtMs} />
      </>
    )
  : <ChestCountdown unlockAtMs={unlockAtMs} />;

    const meta = getChestMetaFromChest(slot);

    return (
      <ChestCard
  key={slot.id ?? i}
  appearDelay={i * 0.05}
  isProcessing={isUnlocking || isOpening}
  label={isLocked ? "OPEN NOW" : meta.label}
  image={meta.image}
  badgeText={badgeText}
  showClock={showClock}
  isClickable={isClickable}
  isReady={isReady}
  isStart={notStarted}
  cardStyle={
    canSpeedup
      ? {
          boxShadow:
            "0 0 12px rgba(255, 223, 95, 0.85), 0 0 22px rgba(255, 223, 95, 0.35)",
        }
      : undefined
  }
  onClick={() => {
    handleChestClick(originalIndex);
  }}
/>
    );
  })}
</div>
)}

    </div>

      {/* Rewards (unchanged) */}
      {rewardsWins.length > 0 && (
        <>
          <div
            style={{
              color: "#ffdf5f",
              textAlign: "left",
              marginTop: 10,
              fontSize: 18,
            }}
          >
            Wins Rewards
          </div>
          {rewardsWins.map((r, i) => (
            <RewardRow key={`w_${i}`} {...r} />
          ))}
        </>
      )}

<AnimatePresence>
  {showTrophySheet && rewardsTrophies.length > 0 && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={closeTrophySheet}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0.12}
        onDragEnd={(e, info) => {
          // swipe down to close
          if (info.offset.y > 120 || info.velocity.y > 800) closeTrophySheet();
        }}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          backgroundColor: "#070000",
          backgroundImage: `
            url("/v2/main_bg.png")
          `,
          backgroundRepeat: "repeat",
          backgroundSize: "110%",
          backgroundPosition: "left top",
          boxShadow: "0 -12px 30px rgba(0,0,0,0.6)",
          padding: 12,
          paddingBottom: "max(18px, env(safe-area-inset-bottom))",
          maxHeight: "75dvh",
          overflow: "hidden",
        }}
      >
        {/* Grab handle */}
        <div
          style={{
            width: 54,
            height: 6,
            borderRadius: 999,
            background: "rgba(255,255,255,0.18)",
            margin: "6px auto 10px",
          }}
        />

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 6px 15px",
          }}
        >
          <div className="shadow2" style={{ fontWeight: 600, marginTop: "-15px",fontFamily: '"Fredoka", sans-serif',color: "#fff",fontSize: 24}}>
            Trophy Rewards
          </div>

          <button
            onClick={closeTrophySheet}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              opacity: 0.95,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "-12px"
            }}
          >
            <img
              src="/v2/close.png"
              alt=""
              style={{
                width: 34,
                height: 34,
                display: "block",
                pointerEvents: "none",
              }}
            />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", maxHeight: "calc(75dvh - 64px)", paddingRight: 4 }}>
          <div style={rewardsWithBarWrapStyle}>
            {/* LEFT: vertical progress bar */}
            <div style={verticalBarColStyle}>
              <div style={verticalTrackStyle}>
                <div style={verticalFillStyle(verticalPct)} />
              </div>

              {/* Markers (your corrected % positioning version) */}
              {(() => {
                const milestonesTopToBottom = [...trophyMilestones].sort((a, b) => a - b);
                const range = Math.max(1, arenaMax - arenaMin);

                return milestonesTopToBottom.map((milestone, i) => {
                  const markerPct =
                    ((Math.max(arenaMin, Math.min(milestone, arenaMax)) - arenaMin) / range) * 100;

                  return (
                    <React.Fragment key={`mk_${milestone}_${i}`}>
                      <div
                        style={{
                          ...verticalMarkerDotStyle,
                          top: `${markerPct}%`,
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                      <div style={{ ...verticalMarkerLabelStyle, top: `${markerPct}%` }}>
                        {milestone}
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>

            {/* RIGHT: existing rows */}
            <div style={{ flex: 1, minWidth: 0,fontFamily: '"Fredoka"' }}>
              {rewardsTrophies.map((r, i) => (
                <RewardRow key={`t_${i}`} {...r} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

<AnimatePresence>
  {defenseDeckRequiredOpen && (
    <div className="modal_box">

      <div className="modal_title">
        OOPS!
        <button className="modal_close" onClick={() => setDefenseDeckRequiredOpen(false)}></button>
      </div>
      <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
                <div className="modal_inside">
        <img
          src="/shield.png"
          alt="Defense Deck"
          style={{
            height: 76,
            filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.45))",
          }}
          onError={(e) => {
            e.currentTarget.src = "/finger.png";
          }}
        />

        <h2
          style={{
            color: "#fff",
                fontSize: "24px",
                margin: 10,
            textShadow: `
              -1px -1px 0 #000,
               1px -1px 0 #000,
              -1px  1px 0 #000,
               1px  1px 0 #000,
               0px  3px 0 #000,
               0px  4px 2px rgba(0,0,0,0.75)
            `,
          }}
        >
          Defense Deck Required
        </h2>

        <div className="info">
          You need to set your Defense Deck before starting a battle.
        </div>
        </div>

        <div className="modal_footer">
          <button className="game-button green-button" onClick={goToDefenseDeck} style={{marginRight: 15}}>
            Defense Deck
          </button>

          <button className="game-button red-button" onClick={() => setDefenseDeckRequiredOpen(false)}>
            LATER
          </button>
        </div>
        
      </motion.div>
    </div>
  )}
</AnimatePresence>

<ArenasHubModal
  open={arenaHubOpen}
  onClose={() => setArenaHubOpen(false)}
>
  <ArenasHub
    onNavigate={handleArenasHubNavigate}
    user={me ?? user}
    setUser={setUser}
    arenaIndex={arenaIndex}
    arenaId={arena?.id}
  />
</ArenasHubModal>


{DEBUG_MODALS && (
  <div
    style={{
      position: "fixed",
      right: 8,
      bottom: 8,
      zIndex: 99999,
      display: "grid",
      gap: 6,
      background: "rgba(0,0,0,0.75)",
      padding: 8,
      borderRadius: 10,
    }}
  >
    <button
      onClick={() =>
        enqueueModal({
          type: "INFO",
          payload: { message: "Debug INFO modal." },
        })
      }
    >
      INFO
    </button>

    <button
      onClick={() =>
        enqueueModal({
          type: "CHEST_OPENED",
          payload: debugPrizePayload,
        })
      }
    >
      CHEST_OPENED
    </button>

    <button
      onClick={() =>
        enqueueModal({
          type: "CHEST_WON",
          payload: {
            id: "debug_chest_won",
            chestType: "wood",
            unlockAt: Date.now() + 60 * 60 * 1000,
          },
        })
      }
    >
      CHEST_WON
    </button>

    <button
      onClick={() =>
        enqueueModal({
          type: "ALL_ARENAS_COMPLETED",
          payload: { name: arena?.name ?? "Final Arena" },
        })
      }
    >
      ALL_COMPLETED
    </button>

    <button
      onClick={() =>
        enqueueModal({
          type: "ARENA_UNLOCKED",
          payload: { name: arena?.name ?? "Debug Arena" },
        })
      }
    >
      ARENA_UNLOCKED
    </button>

    <button onClick={() => setDefenseDeckRequiredOpen(true)}>
      DEFENSE_DECK
    </button>

    <button onClick={() => setArenaHubOpen(true)}>
      ARENAS_HUB
    </button>

    <button
      onClick={() => {
        setSpeedupTargetChest({
          id: "debug_speedup",
          chestType: "wood",
          unlockAt: Date.now() + 90 * 60 * 1000,
          earnedAt: Date.now() - 10 * 60 * 1000,
        });
        setSpeedupError("");
        setSpeedupSuccess("");
        setSpeedupModalOpen(true);
      }}
    >
      SPEEDUP
    </button>
  </div>
)}

<FightPickerModal
  open={fightPickerOpen}
  user={me || user}
  onClose={() => setFightPickerOpen(false)}
  onSelectDone={handleFightPickerSelected}
  onNavigate={onNavigate}
/>


<AnimatePresence>
  {fightRefillOpen && (
    <motion.div
      className="modal_box"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ zIndex: 10000 }}
    >
      <div className="modal_title">
        Out Of Battles

        <button
  className="modal_close"
  onClick={() => {
    setFightRefillOpen(false);
    setFightRefillLoading(false);
    setFightRefillError("");
  }}
  aria-label="Close"
/>
      </div>

      <motion.div
        className="modal_body"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
      >
        <div className="modal_inside">
          <img
            src="/energy.png"
            alt="Extra battle"
            style={{
              height: 90,
              marginTop: -10,
              marginBottom: -5,
            }}
          />

          <div
            className="shadow2"
            style={{
              fontSize: 28,
              fontWeight: 600,
              color: "#fff",
              marginBottom: 8,
            }}
          >
            Need Another Battle?
          </div>

          <div className="info"
            style={{
              fontSize: 18,
              lineHeight: "22px",
            }}
          >
            This fighter has used all battles for today. Watch a short video to unlock
            <strong style={{ color: "#ffdf5f" }}> +1 battle</strong>.

            <div
  style={{
    fontSize: 24,
    marginTop: 8,
    minHeight: 29,
    lineHeight: "29px",
  }}
>
  {fightRefillInfo ? (
    <>
      Refills used today:{" "}
      <strong style={{ color: "#ffdf5f" }}>
        {fightRefillInfo.used}/{fightRefillInfo.limit}
      </strong>
    </>
  ) : (
    <span style={{ visibility: "hidden" }}>
      Refills used today: 0/0
    </span>
  )}
</div>

          </div>

            <div
  className="info"
  style={{
    marginTop: 20,
    fontSize: 16,
    lineHeight: "23px",
  }}
>
  
  <div style={{ color: "#ffdf5f",marginBottom: 8 }}>
            Battle more every day with OG Pass:
            </div>

  <div>
    Free Players: <strong>2 Refills/day</strong>
  </div>

  <div>
    OG Pass: <strong style={{ color: "#ffdf5f" }}>4 Refills/day</strong>
  </div>

  <div>
    OG Pass Plus:{" "}
    <strong style={{ color: "#ffdf5f" }}>6 Refills/day</strong>
  </div>

            <div style={{ marginTop: 10 }}>
  <button
    type="button"
    className="game-button-small green-button"
    style={{ marginRight: 10 }}
    onClick={() => {
      setFightRefillOpen(false);
      setFightRefillError("");
      onNavigate?.("gamepass");
    }}
  >
    Get OG Pass
  </button>

  <button
    type="button"
    className="game-button-small green-button"
    onClick={() => {
      setFightRefillOpen(false);
      setFightRefillError("");
      onNavigate?.("battlePass");
    }}
  >
    Get OG Pass PLUS
  </button>
</div>
</div>

          {fightRefillError && (
            <div className="info"
              style={{
                marginTop: 20,
                color: "#ff6b6b",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {fightRefillError}
            </div>
          )}
        </div>

        <div className="modal_footer">
          <button
            type="button"
            className="game-button green-button"
            disabled={
  fightRefillLoading ||
  !fightRefillInfo ||
  fightRefillInfo.remaining <= 0
}
            onClick={handleFightRefillVideo}
            style={{
            marginRight: 10,
              opacity: fightRefillLoading ? 0.6 : 1,
              cursor: fightRefillLoading ? "wait" : "pointer",
            }}
          >
            {fightRefillLoading
  ? "LOADING..."
  : !fightRefillInfo
  ? "CHECKING..."
  : fightRefillInfo.remaining <= 0
  ? "DAILY LIMIT"
  : "WATCH NOW"}
          </button>

          <button
  type="button"
  className="game-button yellow-button"
  disabled={fightRefillLoading}
  onClick={() => {
    setFightRefillOpen(false);
    setFightRefillError("");
    setFightPickerOpen(true);
  }}
>
  CHOOSE CARD
</button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

<MyTapisPage
  open={showMyTapisModal}
  user={me ?? user}
  setUser={(updater) => {
    setMe((prev) => {
      const base = prev ?? user ?? {};

      return typeof updater === "function"
        ? updater(base)
        : updater;
    });

    setUser?.((prev) => {
      const base = prev ?? user ?? {};

      return typeof updater === "function"
        ? updater(base)
        : updater;
    });
  }}
  onClose={() => setShowMyTapisModal(false)}
/>

      {/* Modal Queue Renderer (unchanged) */}
      {activeModal?.type === "INFO" && (
        
        <SimpleModal open={true} title="OOPS!" onClose={closeActiveModal}>
          <img src="/finger.png" style={{marginBottom: 10,display: "inline-block", height: 90}}/>
          <div className="shadow2" style={{fontWeight: 600,fontSize: 21}}>{activeModal.payload.message}</div>
        </SimpleModal>
      )}

      {activeModal?.type === "CHEST_WON" && (() => {
  const wonMeta = getChestMetaFromChest(activeModal.payload);

  return (
    <ChestOpenedModal
      open={true}
      chest={{
        ...wonMeta,
        unlockAt: activeModal.payload.unlockAt,
      }}
      prizes={[]}          // IMPORTANT: empty → this is NOT an opened chest
      onClose={closeActiveModal}
                onNavigate={onNavigate}
    />
  );
})()}


      {activeModal?.type === "ALL_ARENAS_COMPLETED" && (
        <SimpleModal
          open={true}
          title="🏆 Congratulations!"
          onClose={closeActiveModal}
          footer={<button onClick={closeActiveModal}>Awesome!</button>}
        >
          <div style={{ marginTop: 10 }}>
            You have completed <b>all arenas</b>!
            <div style={{ marginTop: 10, opacity: 0.9 }}>
              Final arena: <b>{activeModal.payload?.name}</b>
            </div>
          </div>
        </SimpleModal>
      )}

                <SimpleModal
  open={speedupModalOpen}
  title="Speed Up Chest"
  onClose={() => {
    if (speedupLoading) return;
    setSpeedupModalOpen(false);
    setSpeedupTargetChest(null);
    setSpeedupError("");
    setSpeedupSuccess("");
  }}

  footer={
    <div>
      <button
        className="game-button green-button"
            style={{marginRight: 15}}
        onClick={confirmChestSpeedup}
        disabled={
          speedupLoading ||
          !getSpeedupPayment(
            getChestSpeedupCfg(
              chestSpeedupByType,
              normalizeChestKey(speedupTargetChest?.chestType ?? speedupTargetChest?.chest_type)
            )
          ).enabled
        }
      >
        {speedupLoading ? "Processing..." : "Confirm"}
      </button>

            <button
        className="game-button red-button"
        onClick={() => {
          setSpeedupModalOpen(false);
          setSpeedupTargetChest(null);
          setSpeedupError("");
          setSpeedupSuccess("");
        }}
        disabled={speedupLoading}
      >
        CANCEL
      </button>
    </div>
  }
>
  {(() => {
    const chestType = normalizeChestKey(speedupTargetChest?.chestType);
    const cfg = getChestSpeedupCfg(chestSpeedupByType, chestType);
    const unlockAtMs = toMsTimestamp(
      speedupTargetChest?.unlockAt ??
      speedupTargetChest?.unlock_at ??
      speedupTargetChest?.unlockAtMs ??
      speedupTargetChest?.unlock_at_ms,
      0
    );
    const remainingMs = Math.max(0, unlockAtMs - Date.now());
    const remainingSeconds = Math.ceil(remainingMs / 1000);

    return (
      <div>
        <div style={{ marginBottom: 10,textShadow: `
              -1px -1px 0 #000,
               1px -1px 0 #000,
              -1px  1px 0 #000,
               1px  1px 0 #000,
               0px  3px 0 #000,
               0px  4px 2px rgba(0,0,0,0.75)
            `,fontWeight: 600, fontSize: "21px" }}>
            <img src="/clock.png" style={{height: 42,marginBottom: 10}}/><br/>
          This will remove <strong>{formatSpeedupTime(cfg.time)}</strong> from this chest cooldown.
        </div>

            <div style={{ fontWeight: 600,marginTop:20,marginBottom: 10,textShadow: `
              -1px -1px 0 #000,
               1px -1px 0 #000,
              -1px  1px 0 #000,
               1px  1px 0 #000,
               0px  1px 0 #000,
               0px  1px 1px rgba(0,0,0,0.75)
            ` }}>
          Current remaining time: <strong>{formatSpeedupTime(remainingSeconds)}</strong>
        </div>

       {(() => {
        const payment = getSpeedupPayment(cfg);

        const iconSrc =
          payment.currency === "runes" ? "/runes.png" :
          payment.currency === "sparks" ? "/sparks.png" :
          null;

        return (
          <div
            className="info"
            style={{
              fontSize: "21px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <span>Cost:</span>

            {payment.enabled ? (
              <strong
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {iconSrc && (
                  <img
                    src={iconSrc}
                    alt={payment.currency}
                    style={{
                      width: 22,
                      height: 22,
                      objectFit: "contain",
                    }}
                  />
                )}
                {payment.amount}
              </strong>
            ) : (
              <strong>Disabled</strong>
            )}
          </div>
        );
      })()}

        {speedupSuccess ? (
          <div style={{ color: "#7CFC98", marginTop: 10 }}>{speedupSuccess}</div>
        ) : null}

        {speedupError ? (
          <div style={{ color: "#ff8f8f", marginTop: 10 }}>{speedupError}</div>
        ) : null}
      </div>
    );
  })()}
</SimpleModal>

                {activeModal?.type === "CHEST_OPENED" && (
  <ChestOpenedModal
    open={true}
    chest={activeModal.payload?.chest}
    prizes={activeModal.payload?.prizes}
    onClose={closeActiveModal}
    onNavigate={onNavigate}
  />
)}


      {activeModal?.type === "ARENA_UNLOCKED" && (() => {
  const nextArenaIndex = Math.min(arenaIndex + 1, arenasList.length - 1);
  const nextArena = arenasList[nextArenaIndex];

  const modalArena = {
    ...nextArena,
    ...(activeModal.payload || {}),
  };

  const arenaImageSrc = modalArena?.imageUrl
    ? `/v2/${modalArena.imageUrl}`
    : "./arena.png";

  const goToNextArena = () => {
    closeActiveModal();
    setArenaIndex(nextArenaIndex);
  };

  return (
    <SimpleModal
      open={true}
      title="Congratulations!"
      onClose={goToNextArena}
      footer={
        <button
          className="game-button green-button"
          onClick={goToNextArena}
        >
          Go to Next Arena
        </button>
      }
    >
      <img
        src={arenaImageSrc}
        alt={modalArena?.name || "Arena"}
        style={{
          height: 130,
          maxWidth: "100%",
          objectFit: "contain",
          marginTop: -5,
          marginBottom: 10,
          filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.55))",
        }}
      />

      <div className="info" style={{ fontSize: 21, marginTop: 10 }}>
        <b>{modalArena?.name || "Arena"} Unlocked!</b>
      </div>
    </SimpleModal>
  );
})()}
    </div>
  );
}

export default Arenas;
